import { CRUDFabric } from "../CRUDs/CRUDFabric";
import { Invoice } from "../CRUDs/entities/Invoice";
import { Job } from "../CRUDs/entities/Job";
import { JobRequest } from "../CRUDs/entities/JobRequest";
import { Visit } from "../CRUDs/entities/Visit";
import { reportRecordOpeningError } from "../Common/reportRecordOpeningError";
import worker_cabinet from "../worker_cabinet";

export class UnversalModal {

    constructor(customer_id, modal_container = 'form-container-s', options = {}) {
        if (Number.isInteger(customer_id)) {
            this.customer_id = customer_id;
            this.modal_container = modal_container;
            this.syncUrl = options.syncUrl !== false;
            this.modal_id = 'u-customer-modal';
            this.currentInternalStep = null;
            this.currentStep = null;
            this.internalStepType = null;
            this.internalType = null;
            this.steps_with_internal_navigation = [
                'projects',
                'appointments',
                'subscriptions',
            ]
            this.parent_entity_id = null;
            this.parentLocalId = null;
            this.internalEntityId = null;
            this.internalEntityLocalId = null;
        } else {
            throw new Error("Customer id not entered!");
        }
    }

    positiveIntegerId(value) {
        const text = String(value ?? '').trim();
        return /^[1-9]\d*$/.test(text) ? text : null;
    }

    reportRecordOpeningError(message) {
        reportRecordOpeningError(message);
    }

    // Browser history uses organisation-local IDs. The existing POST state
    // remains on database primary IDs.
    saveModalStateToUrl(
        step,
        internalStep = null,
        parentId = null,
        internalEntityId = null,
        parentLocalId = null,
        internalEntityLocalId = null
    ) {
        if (!this.syncUrl) {
            return;
        }

        const url = new URL(window.location);
        url.searchParams.set('modal', 'universal');
        url.searchParams.set('customer_id', this.customer_id);
        url.searchParams.set('step', step);
        ['internal_step', 'parent_local_id', 'entity_local_id']
            .forEach((key) => url.searchParams.delete(key));

        if (internalStep) {
            url.searchParams.set('internal_step', internalStep);
        }

        if (parentId) {
            const localId = this.positiveIntegerId(parentLocalId ?? this.parentLocalId);
            if (!localId) {
                this.reportRecordOpeningError('This record has no valid organization ID and cannot be linked.');
                return false;
            }
            url.searchParams.set('parent_local_id', localId);
        }

        if (internalEntityId) {
            const localId = this.positiveIntegerId(internalEntityLocalId ?? this.internalEntityLocalId);
            if (!localId) {
                this.reportRecordOpeningError('This selected record has no valid organization ID and cannot be linked.');
                return false;
            }
            url.searchParams.set('entity_local_id', localId);
        }

        window.history.replaceState({}, '', url);
        return true;
    }

    // Method to load modal state from URL parameters
    loadModalStateFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        const modalType = urlParams.get('modal');
        const customerId = urlParams.get('customer_id');
        const requestedStep = urlParams.get('step');
        const supportedSteps = ['info', 'projects', 'appointments', 'subscriptions', 'overview'];
        const step = requestedStep && supportedSteps.includes(requestedStep) ? requestedStep : null;
        const internalStep = urlParams.get('internal_step');

        if (modalType === 'universal' && requestedStep && !step) {
            this.reportRecordOpeningError('This link contains an unsupported modal step.');
            return false;
        }

        // Check if we should open the universal modal
        if (modalType === 'universal' && customerId == this.customer_id && step) {
            const identifiers = {
                parentLocalId: urlParams.get('parent_local_id'),
                entityLocalId: urlParams.get('entity_local_id'),
            };

            if (internalStep && ['projects', 'appointments', 'subscriptions', 'overview'].includes(step)) {
                this.openModalFromUrl(step, internalStep, identifiers);
            } else {
                this.openModal(step, internalStep);
            }
            return true;
        }
        return false;
    }

    /**
     * Returns 1rem in pixels for current document (responsive to root font size).
     */
    #getRemPx() {
        return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    }

    /**
     * Updates loader position/size: does not cover modal-navigation, modal-actions, adds gaps and horizontal padding.
     * Gaps use rem so they scale with screen/root font; horizontal padding 30px as per content area.
     */
    #updateModalLoaderRect() {
        const remPx = this.#getRemPx();
        const gapUnderNav = 0.75 * remPx;   // 0.75rem under .modal-navigation
        const gapAboveActions = 0.75 * remPx;
        const horizontalPadding = 30;       // 30px left/right from content
        const $modal = $(`#${this.modal_id}`);
        const $content = $modal.find('.modal-content');
        const $body = $modal.find('.modal-body');
        const $navMain = $modal.find('.modal-navigation-main');
        const $actions = $modal.find('.modal-actions');
        const $loader = $modal.find('.loader-bg');
        if (!$content.length || !$body.length || !$loader.length) return;
        const contentTop = $content.offset().top;
        const bodyTop = $body.offset().top;
        const bodyScrollHeight = $body.get(0).scrollHeight;
        const bodyBottom = bodyTop + bodyScrollHeight;
        // Start below .modal-navigation-main only (modal-navigation-internal is covered by loader)
        const $nav = $modal.find('.modal-navigation');
        const navBottom = $navMain.length
            ? $navMain.offset().top + $navMain.outerHeight()
            : ($nav.length ? $nav.offset().top + $nav.outerHeight() : bodyTop);
        const loaderTop = navBottom + gapUnderNav - contentTop;
        const actionsTop = $actions.length ? $actions.offset().top : bodyBottom;
        let loaderBottom = $actions.length ? actionsTop - gapAboveActions : bodyBottom;
        // Extend loader at least to the bottom of .info-container so it fully covers the content area
        const $infoContainer = $modal.find('.info-container').first();
        if ($infoContainer.length) {
            const infoContainerEl = $infoContainer.get(0);
            const infoContainerBottom = $infoContainer.offset().top + (infoContainerEl.scrollHeight || $infoContainer.outerHeight());
            loaderBottom = Math.max(loaderBottom, infoContainerBottom);
        }
        const loaderHeight = Math.max(loaderBottom - (navBottom + gapUnderNav), 200);
        $loader.css({
            top: loaderTop + 'px',
            left: horizontalPadding + 'px',
            right: horizontalPadding + 'px',
            width: `calc(100% - ${horizontalPadding * 2}px)`,
            height: loaderHeight + 'px',
            background: '#e0e0e0',
            opacity: 1
        });
    }

    /**
     * Shows loader over modal body and observes content area so loader expands when new content appears.
     */
    showModalLoader() {
        const $modal = $(`#${this.modal_id}`);
        const $body = $modal.find('.modal-body');
        const $loader = $modal.find('.loader-bg');
        if (!$body.length || !$loader.length) return;
        this.#updateModalLoaderRect();
        $loader.show();
        const bodyEl = $body.get(0);
        const resizeObserver = new ResizeObserver(() => this.#updateModalLoaderRect());
        resizeObserver.observe(bodyEl);
        const scheduleUpdate = () => {
            if (this._loaderRafId) cancelAnimationFrame(this._loaderRafId);
            this._loaderRafId = requestAnimationFrame(() => {
                this.#updateModalLoaderRect();
                this._loaderRafId = null;
            });
        };
        const mutationObserver = new MutationObserver(scheduleUpdate);
        mutationObserver.observe(bodyEl, { childList: true, subtree: true });
        this._loaderResizeObserver = resizeObserver;
        this._loaderMutationObserver = mutationObserver;
    }

    /**
     * Hides loader and disconnects observers.
     */
    hideModalLoader() {
        const $loader = $(`#${this.modal_id}`).find('.loader-bg');
        if (this._loaderRafId) {
            cancelAnimationFrame(this._loaderRafId);
            this._loaderRafId = null;
        }
        if (this._loaderResizeObserver) {
            this._loaderResizeObserver.disconnect();
            this._loaderResizeObserver = null;
        }
        if (this._loaderMutationObserver) {
            this._loaderMutationObserver.disconnect();
            this._loaderMutationObserver = null;
        }
        $loader.hide();
    }

    setModalContainer(modal_container = 'form-container-s'){
        this.modal_container = modal_container;
    }

    getModal(successCallback = ()=>{}, errorCallback = ()=>{}){
        const data = {
            type: 'universal',
        };
        $.ajax({
            method: 'POST',
            url: `/customers/${this.customer_id}/get-info-modal`,
            data: data,
            beforeSend: ()=>{
                //$('.bb-preloader-container').show();
            },
            success:(response)=>{
                if (response.view) {
                    this.pasteModal(response.view);
                }else{
                    this.pasteModal(response);
                }
                successCallback();
            },
            error:(response)=>{
                $('.bb-preloader-container').hide();
                errorCallback(response);
            },
            complete: ()=>{
                $('.bb-preloader-container').hide();
                this.resizeCommentField();
                this.showMore();
            }
        });
    }

    pasteModal(modal_html){
        $(`#${this.modal_container}`).find(`#${this.modal_id}`).remove();
        $(`#${this.modal_container}`).append(modal_html);
    }

    pasteInternalNavigation(internal_navigation_html){
        if (internal_navigation_html !== "") {
            $(`#${this.modal_id}`).find('.modal-navigation-internal').html(internal_navigation_html);
            $(`#${this.modal_id}`).find('.modal-navigation-internal').removeClass('d-none');

            $(`#${this.modal_id}`).find('.modal-navigation-internal-mobile .steps-container').html(internal_navigation_html);
            $(`#${this.modal_id} .modal-navigation-internal-mobile`).find('.back-btn').parent().remove();
            $(`#${this.modal_id}`).find('.modal-navigation-internal-mobile').removeClass('d-none');

        } else {
            $(`#${this.modal_id}`).find('.modal-navigation-internal').addClass('d-none');
            $(`#${this.modal_id}`).find('.modal-navigation-internal-mobile').addClass('d-none');
        }
    }

    pasteStepActions(actions_html){
        if (actions_html !=='') {
            $(`#${this.modal_id}`).find('.modal-actions').html(actions_html);
        }else{
            $(`#${this.modal_id}`).find('.modal-actions').html("");
        }
    }

    pasteStepInfo(step_info_html){
        $(`#${this.modal_id}`).find('.data-container').html(step_info_html);
        if ($(`#${this.modal_id}`).find('.data-container').hasClass('d-none')) {
            $(`#${this.modal_id}`).find('.data-container').removeClass('d-none');
        }
        $(`#${this.modal_id}`).find('.internal-data-container').addClass('d-none');
    }

    pasteItnernalStepInfo(internal_step_info_html){
        $(`#${this.modal_id}`).find('.internal-data-container').html(internal_step_info_html);
        if ($(`#${this.modal_id}`).find('.internal-data-container').hasClass('d-none')) {
            $(`#${this.modal_id}`).find('.internal-data-container').removeClass('d-none');
        }
        const selectedRecord = $(`#${this.modal_id}`).find('.is-selected-record').get(0);
        if (selectedRecord) {
            selectedRecord.scrollIntoView({ block: 'nearest' });
        }
        $(`#${this.modal_id}`).find('.data-container').addClass('d-none');
    }

    openModalFromUrl(step, internalStep, identifiers = {}) {
        const parentLocalId = this.positiveIntegerId(identifiers.parentLocalId);
        const entityLocalId = identifiers.entityLocalId == null
            ? null
            : this.positiveIntegerId(identifiers.entityLocalId);
        if (!parentLocalId || (identifiers.entityLocalId != null && !entityLocalId)) {
            this.reportRecordOpeningError('This link contains an invalid record ID.');
            return;
        }

        const data = {
            step: step,
            internal_step: internalStep,
            parent_local_id: parentLocalId,
        };

        if (entityLocalId) data.entity_local_id = entityLocalId;

        $.ajax({
            method: 'GET',
            url: `/customers/${this.customer_id}/resolve-modal-record`,
            data: data,
            success: (resolved) => {
                this.openModal(
                    step,
                    internalStep,
                    resolved.parent_id,
                    resolved.entity_id,
                    {
                        parentLocalId: resolved.parent_local_id,
                        entityLocalId: resolved.entity_local_id,
                    }
                );
            },
            error: (response) => {
                const message = response?.responseJSON?.message
                    || 'This link cannot identify one exact record. Open it again from the current list.';
                this.reportRecordOpeningError(message);
            },
        });
    }

    openModal(
        step = 'info',
        internal_step = null,
        parent_id = null,
        entity_id = null,
        localIds = {},
        callbacks = {}
    ) {
        const supportedSteps = ['info', 'projects', 'appointments', 'subscriptions', 'overview'];
        if (!supportedSteps.includes(step)) {
            this.reportRecordOpeningError('This link contains an unsupported modal step.');
            return;
        }

        this.parent_entity_id = parent_id;
        this.internalEntityId = entity_id;
        this.parentLocalId = localIds.parentLocalId ?? null;
        this.internalEntityLocalId = localIds.entityLocalId ?? null;

        // Save current modal state to URL
        this.saveModalStateToUrl(
            step,
            internal_step,
            parent_id,
            entity_id,
            this.parentLocalId,
            this.internalEntityLocalId
        );

        this.getModal(() => {
            $(`#${this.modal_id}`).modal('show');
            this.activateNavigationListeners();
            this.#getStepData(step, {}, 0, () => {
                // Main tab "info" (user info) has no internal-step handler on the server; only load internal content for other steps.
                if (internal_step && step !== 'info') {
                    if (step == 'projects') {
                        step = 'request';
                        this.parent_entity_id = parent_id;
                    } else if (step == 'appointments') {
                        step = 'appointment';
                        this.parent_entity_id = parent_id
                    } else if (step == 'subscriptions') {
                        step = 'subscription';
                        this.parent_entity_id = parent_id
                    } else if (step == 'overview') {
                        if (internal_step === 'appointment') {
                            step = 'appointment';
                        } else if (internal_step === 'invoice') {
                            step = 'request';
                        } else if (internal_step === 'job') {
                            step = 'request';
                        } else if (internal_step === 'request') {
                            step = 'request';
                        }

                        this.parent_entity_id = parent_id
                    }

                    if (entity_id) {
                        this.#getInternalStepData(entity_id, step, internal_step);
                    } else {
                        this.#getInternalStepData(null, step, internal_step);
                    }
                }
            });
        }, callbacks.onError);
    }

    activateNavigationListeners(){
        // CRUDEntity.update() announces a successful save with an `entity_updated` event on
        // document, but the only listener in the app was the CUSTOMER cabinet — nothing on
        // the business side re-fetched, so this window kept rendering the pre-save values
        // until a manual reload. Re-pull whatever is on screen instead.
        // Namespaced + .off() first so reopening the modal cannot stack duplicate handlers.
        $(document).off('entity_updated.universalModalRefresh')
        .on('entity_updated.universalModalRefresh', () => {
            if (!$(`#${this.modal_id}`).hasClass('show')) return;   // modal closed: nothing to refresh
            const options = typeof this.collectOptionsForStep === 'function'
                ? this.collectOptionsForStep()
                : {};
            this.#getStepData(this.currentStep, options);
            // If a record detail is open on top of the list, re-pull that too.
            if (this.currentInternalStep) {
                this.#getInternalStepData(this.internalEntityId, this.internalType || 'request', this.currentInternalStep);
            }
        });

        $('.modal-navigation .main-step').off('click').on('click', (e) => {
            const step = $(e.currentTarget).data('type');
            // Save step to URL when navigating
            this.saveModalStateToUrl(step);
            this.#getStepData(step);
            // $('.modal-navigation .main-step').removeClass('active');
            // $(e.currentTarget).addClass('active');
            /* if (this.steps_with_internal_navigation.includes($(e.currentTarget).data('type'))) {
                $('.modal-navigation-internal').removeClass('d-none');
            }else{
                $('.modal-navigation-internal').addClass('d-none');
            } */
        });
        $(`#${this.modal_id}`).off('click', '.modal-navigation-main .change-step')

        .on('click', '.modal-navigation-main .change-step', (e)=>{
            const curr_button = $(e.currentTarget);
            const list = curr_button.parent().find('.main-steps-list');
            const next_step = curr_button.hasClass('next') ? true : false;

            if (curr_button.hasClass('opened')) {
                list.addClass('d-none');
                curr_button.removeClass('opened');
                curr_button.find('img').css({transform: 'none'});
            } else {
                $('.modal-navigation-main .change-step').removeClass('opened');
                $('.modal-navigation-main .change-step').find('img').css({transform: 'none'});
                curr_button.addClass('opened');
                list.removeClass('d-none');
                const activeStep =  $('.main-step.focus');
                if (next_step) {
                    curr_button.find('img').css({transform: 'rotate(90deg)'});
                    let posX = 0;
                    let posY = 45;
                    $(`#${this.modal_id} .main-steps-list`).removeAttr('style').css({right: posX, top: posY});
                } else {
                    curr_button.find('img').css({transform: 'rotate(-90deg)'});
                    let posX = 0;
                    let posY = 45;
                    $(`#${this.modal_id} .main-steps-list`).removeAttr('style').css({left: posX, top: posY});
                }

            }
        })
        $(`#${this.modal_id}`).off('click', '.modal-navigation-main .main-steps-list-row')
        .on('click', '.modal-navigation-main .main-steps-list-row', (e)=>{
            $(`#${this.modal_id} .main-steps-list`).addClass('d-none');
            $(`#${this.modal_id} .modal-navigation-main .change-step img`).css({transform: 'none'});
            $(`#${this.modal_id} .modal-navigation-main .change-step.opened`).removeClass('opened');
            $(`#${this.modal_id} .main-step.active`).removeClass('active');
            $(`#${this.modal_id} .main-step[data-type=${$(e.currentTarget).data('step')}]`).addClass('active');
            $(`#${this.modal_id} .modal-navigation-main .main-step[data-type=${$(e.currentTarget).data('step')}]`).click();
        });
        $(`#${this.modal_id}`).off('click', '.modal-navigation-main .main-steps-list-row')
        .on('click', '.modal-navigation-main .main-steps-list-row', (e)=>{
            $(`#${this.modal_id} .main-steps-list`).addClass('d-none');
            $(`#${this.modal_id} .modal-navigation-main .change-step img`).css({transform: 'none'});
            $(`#${this.modal_id} .modal-navigation-main .change-step.opened`).removeClass('opened');
            $(`#${this.modal_id} .main-step.active`).removeClass('active');
            $(`#${this.modal_id} .main-step[data-type=${$(e.currentTarget).data('step')}]`).addClass('active');
            $(`#${this.modal_id} .modal-navigation-main .main-step[data-type=${$(e.currentTarget).data('step')}]`).click();
        });
    }

    #getStepData(step = 'info', data = {}, delay = 0, successCallback = ()=>{}){
        let dataOptions = data;
        dataOptions['step'] = step;
        $('.modal-navigation .main-step').removeClass('active');
        $(`.modal-navigation .main-step[data-type="${step}"]`).addClass('active');
        // Clear content area immediately so previous tab content (info-container) is fully hidden
        const $modal = $(`#${this.modal_id}`);
        $modal.find('.data-container').html('').removeClass('d-none');
        $modal.find('.internal-data-container').addClass('d-none');
        let timeout = setTimeout(() => {
            $.ajax({
                method: 'POST',
                url: `/customers/${this.customer_id}/get-step-info`,
                data: dataOptions,
                beforeSend: ()=>{
                    this.showModalLoader();
                },
                success: (response)=>{
                    this.currentStep = step;
                    if (response.error) {
                        console.error('[get-step-info]', response.error, response.error_file || '');
                    }
                    if (response.view) {
                        this.pasteStepInfo(response.view);
                    }else{
                        this.pasteStepInfo(response);
                    }

                    if (response.internal_navigation) {
                        this.pasteInternalNavigation(response.internal_navigation);
                    }else{
                        this.pasteInternalNavigation("");
                    }
                    if (response.actions) {
                        this.pasteStepActions(response.actions);
                    } else {
                        this.pasteStepActions("");
                    }
                    switch (step) {
                        case 'info':
                            this.activateInfoListeners();
                            this.initInfoMap();
                            break;
                        case 'projects':
                            this.activateProjectListeners();
                            // Update pagination after loading projects data
                            setTimeout(() => {
                                this.updateCustomerPagination();
                            }, 0);
                            break;
                        case 'appointments':
                            this.activateAppointmentsListeners();
                            // Update pagination after loading appointments data
                            setTimeout(() => {
                                this.updateCustomerPagination();
                            }, 0);
                            break;
                        case 'subscriptions':
                            this.activateSubscriptionsListeners();
                            // Update pagination after loading subscriptions data
                            setTimeout(() => {
                                this.updateCustomerPagination();
                            }, 0);
                            break;
                        case 'overview':
                            this.activateOverviewListeners();
                            // Update pagination after loading overview data
                            setTimeout(() => {
                                this.updateCustomerPagination();
                            }, 0);
                            break;
                        default:
                            break;
                    }
                    let reset_date_flag = true;
                    if (data["date-range"] && data["date-range"] !== "") {
                        reset_date_flag = false;
                    }

                    this.initDataInput(reset_date_flag);
                    this.initStatusSelect();
                    this.initResetFiltersButton();
                    this.changeFileVisibility();
                    this.hideModalLoader();

                    successCallback();
                },
                error: (response) => {
                    this.hideModalLoader();
                },
                complete: ()=>{
                }
            });
        }, delay);
        clearTimeout(timeout-1);
    }

    /**
     *
     * @param {Int} entity_id
     * @param {String} type Ex-s:[request, appointment, subscription]
     * @param {String} step Ex-s:[[request, visit, quote, job, invoice],[appointment, payment],[subscription, payment_subscripotion]]
     */
    #getInternalStepData(entity_id = null, type = "request", step = 'job-request', successCallback = () => { }, errorCallback = () => { }, backNavCallback = null) {
        // If entity_id is provided, use it; otherwise use the one from the instance
        if (entity_id !== null) {
            this.internalEntityId = entity_id;
        }

        let data = {
            type: type,
            step: step,
            entity_id: entity_id,
            entity_type: this.internalStepType,
            request_id: this.parent_entity_id
        };

        $.ajax({
            method: 'POST',
            url: `/customers/${this.customer_id}/get-internal-step`,
            data: data,
            beforeSend: () => {
                this.showModalLoader();
            },
            success: (response) => {
                this.currentInternalStep = step;
                this.internalType = type;

                if (response.need_creating) {
                    this.#getInternalStepData(null, 'appointment', 'appointment');
                } else {
                    if (response.internal_navigation) {
                        this.pasteInternalNavigation(response.internal_navigation);
                        this.activateInternalNavigationListeners(backNavCallback);
                    } else {
                        this.pasteInternalNavigation("");
                    }

                    if (response.view) {
                        this.pasteItnernalStepInfo(response.view);
                        this.activateInternalStepActions(step);
                        this.changeStatus();
                        this.initLogs();
                        this.changeFileVisibility();
                    }

                    if (response.actions) {
                        this.pasteStepActions(response.actions);
                    } else {
                        this.pasteStepActions("");
                    }
                }
                if (Object.prototype.hasOwnProperty.call(response, 'entity_id')) {
                    this.internalEntityId = response.entity_id ?? null;
                }
                if (Object.prototype.hasOwnProperty.call(response, 'parent_local_id')) {
                    this.parentLocalId = response.parent_local_id ?? this.parentLocalId;
                }
                if (Object.prototype.hasOwnProperty.call(response, 'entity_local_id')) {
                    this.internalEntityLocalId = response.entity_local_id ?? null;
                }

                // Save state
                this.saveModalStateToUrl(this.currentStep, step, this.parent_entity_id, this.internalEntityId);

                // Выполнить клик только один раз
                if (!this._clickedInternalStepOnce) {
                    this._clickedInternalStepOnce = true;
                    //$($('.internal-step-container.focused').last()).find('.internal-step').click();
                }

                successCallback();
            },
            error: (response) => {
                this.hideModalLoader();
                console.log(response);
                errorCallback();
            },
            complete: () => {
                this.hideModalLoader();
            }
        });
    }

    activateInfoListeners(){
        $(`#${this.modal_id}`).off('click', '.edit-customer-info-btn').on('click', '.edit-customer-info-btn', (e)=>{
            // .edit-text ONLY — the button also holds the .bb-btn__icon span; a bare
            // find('span') wrote "Editing" into BOTH (doubled label + wiped icon)
            if ($(e.currentTarget).find('span.edit-text').text() !== 'Editing') {
                $(e.currentTarget).find('span.edit-text').text('Editing');
                $("input[name='phone']").mask("999-999-9999");
                $("input[name='zip_code']").mask("99999");

                $('.customer-details-table').hide();
                $('.customer-edit-table').show();

                $('#comments').prop('disabled', false);
                $(`#${this.modal_id}`).find('.btn-cancel-editing').toggleClass('d-none');
                $(`#${this.modal_id}`).find('.btn-save').toggleClass('d-none');
                $(`#${this.modal_id}`).find('.btn-secondary-close').toggleClass('d-none');
            }
        });
        $(`#${this.modal_id}`).off('click', '.btn-cancel-editing').on('click', '.btn-cancel-editing',()=>{
            $('.customer-edit-table').hide();
            $('.customer-details-table').show();

            $('#comments').prop('disabled', true);
            isEditing = false;
            $('.edit-text').text('Edit');
            $(`#${this.modal_id}`).find('.btn-cancel-editing').toggleClass('d-none');
            $(`#${this.modal_id}`).find('.btn-save').toggleClass('d-none');
            $(`#${this.modal_id}`).find('.btn-secondary-close').toggleClass('d-none');
        });
        $(`#${this.modal_id}`).off('click', '.btn-save').on('click', '.btn-save',(e)=>{
            const updatedData = {};

            updatedData['first_name'] = $('.customer-edit-table input[name=first_name]').val();
            updatedData['last_name'] = $('.customer-edit-table input[name=last_name]').val();
            updatedData['phone'] = $('.customer-edit-table input[name=phone]').val();
            updatedData['address'] = $('.customer-edit-table input[name=address]').val();
            updatedData['zip_code'] = $('.customer-edit-table input[name=zip_code]').val();
            updatedData['apartment'] = $('.customer-edit-table input[name=apartment]').val();
            updatedData['email'] = $('.customer-edit-table input[name=email]').val(); // Добавлено поле Email
            updatedData['additional_info'] = $('#comments').val();
            updatedData['organisation_name'] = $('.customer-edit-table input[name=organisation_name]').val();
            updatedData.id = this.customer_id;

            if (updatedData['email']) {
                const email = updatedData['email'];
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (emailRegex.test(email)) {
                } else {
                    $('.info-block-errors').show()
                    $('.error-text').text('The address must be a valid Email address')
                }
            }

            $.ajax({
                url: `/customers/${this.customer_id}/update`,
                type: 'POST',
                contentType: 'application/json',
                data: JSON.stringify(updatedData),
                success: (response) => {

                    const $modal = $(`#${this.modal_id}`);
                    const fullName = [updatedData.first_name, updatedData.last_name]
                        .filter(value => value && String(value).trim())
                        .join(' ');
                    const fullAddress = [updatedData.address, updatedData.apartment, updatedData.zip_code]
                        .filter(value => value && String(value).trim())
                        .join(', ');
                    const displayValue = (value) => value && String(value).trim() ? value : '-';

                    $modal.find('.customer-details-table [data-key="name"] .js-customer-details-name')
                        .text(displayValue(fullName));
                    $modal.find('.customer-details-table [data-key="phone"] .description-row-value.static')
                        .text(displayValue(updatedData.phone));
                    $modal.find('.customer-details-table [data-key="address"] .description-row-value.static')
                        .text(displayValue(fullAddress));
                    $modal.find('.customer-details-table [data-key="email"] .description-row-value.static')
                        .text(displayValue(updatedData.email));
                    $modal.find('.customer-details-table [data-key="organisation_name"] .description-row-value.static')
                        .text(displayValue(updatedData.organisation_name));

                    $modal.find('.js-customer-name').text(displayValue(fullName));
                    $modal.find('.js-customer-phone').text(displayValue(updatedData.phone));
                    $modal.find('.js-customer-address').text(displayValue(fullAddress));
                    $modal.find('.js-customer-email').text(displayValue(updatedData.email));

                    // Скрываем таблицу для редактирования и показываем текущую
                    $('.customer-edit-table').hide();
                    $('.customer-details-table').show();

                    $('#comments').prop('disabled', true);

                    $('.edit-text').text('Edit');
                    
                    $(`#${this.modal_id}`).find('.btn-cancel-editing').toggleClass('d-none');
                    $(`#${this.modal_id}`).find('.btn-save').toggleClass('d-none');
                    $(`#${this.modal_id}`).find('.btn-secondary-close').toggleClass('d-none');
                    $('.info-block-errors').hide()
                },
                error: function(xhr, status, error) {
                    console.error('Error:', error);
                }
            });
        });
        $(`#${this.modal_id}`).off('click', '.send-welcome-email')
        .on('click', '.send-welcome-email', (e)=>{
            const customer = {
                id: this.customer_id
            };
            $.ajax({
                type: 'post',
                url: '/customers/send-welcome',
                data: {
                    dataFromArray: customer
                },
                success: (response) => {
                    $('.modal-window-actions.csv-upload').hide();
                },
                error: (error) => {
                    //    console.log(error)
                }
            });
        });
        this.scopeDropZone('customer',this.customer_id);
    }
    initInfoMap(){
        if ($(`#${this.modal_id}`).find('#route-map').length>0) {
            let lat = $('#customer-lat').val();
            let lon = $('#customer-lon').val();
            worker_cabinet.mapInit(true,() =>{
                worker_cabinet.RouteDisplayMap({
                    lat,
                    lon
                });
            });
        }
    }

    initResetFiltersButton(){
        $(`#${this.modal_id}`).off('click', '.reset-filter-btn')
        .on('click', '.reset-filter-btn', (e)=>{
            this.#getStepData(this.currentStep, {});
        });
    }

    activateProjectListeners(){
        /** Select project */
        $(`#${this.modal_id}`).find('.data-container .select-request-btn').off('click')
        .on('click', (e) => {
            this.parent_entity_id = $(e.currentTarget).data('id');
            this.parentLocalId = $(e.currentTarget).data('parent-local-id');
            this.internalEntityLocalId = $(e.currentTarget).data('entity-local-id');
            this.saveModalStateToUrl('projects', null, this.parent_entity_id);
            this.#getInternalStepData(null, 'request', $(e.currentTarget).data('entity-type'));
        });

        // Add filtering functionality
        $(`#${this.modal_id} .data-container`).off('input', '#table_size_select, input[name="date-range"]')
        .on('input', '#table_size_select, input[name="date-range"]', (e)=>{
            if ($(e.currentTarget).attr('name') === 'date-range') {
                // For date range, we need to wait for the apply event
                return;
            }
            this.#getStepData(this.currentStep, this.collectOptionsForStep(), 1000);
        });

        $(`#${this.modal_id} .data-container`).off('change', '#table_size_select, select[name="status"]')
        .on('change', '#table_size_select, select[name="status"]', (e)=>{
            const data = this.collectOptionsForStep(true);
            this.#getStepData(this.currentStep, data);
        });

        // Handle date range apply event
        $(`#${this.modal_id}`).off('apply.daterangepicker', 'input[name="date-range"]')
        .on('apply.daterangepicker', 'input[name="date-range"]', (e, picker) => {
            this.#getStepData(this.currentStep, this.collectOptionsForStep(true));
        });

        // Handle date range cancel event
        $(`#${this.modal_id}`).off('cancel.daterangepicker', 'input[name="date-range"]')
        .on('cancel.daterangepicker', 'input[name="date-range"]', (e, picker) => {
            $(e.currentTarget).val('');
            this.#getStepData(this.currentStep, this.collectOptionsForStep(true));
        });

        // Handle reset filters button
        $(`#${this.modal_id}`).off('click', '.reset-filter-btn')
        .on('click', '.reset-filter-btn', (e) => {
            // Reset all filters
            $(`#${this.modal_id}`).find('input[name="date-range"]').val('');
            $(`#${this.modal_id}`).find('select[name="status"]').val('');
            this.#getStepData(this.currentStep, this.collectOptionsForStep(true));
        });

        // Initialize pagination
        this.initCustomerPagination();

        // Update pagination UI
        setTimeout(() => {
            this.updateCustomerPagination();
        }, 0);

        $(`#${this.modal_id} .internal-data-container`).off('click', '.schedule-container')
        .on('click', '.schedule-container', (e) => {
            const entityId = $(e.currentTarget).data('entity_id');
            const type = $(e.currentTarget).data('type');
            const entityType = $(e.currentTarget).data('entity_type');
            this.internalEntityId = entityId;
            this.internalEntityLocalId = $(e.currentTarget).data('entity_local_id');
            // Save state to URL when selecting a specific entity
            this.saveModalStateToUrl(this.currentStep, entityType, this.parent_entity_id, entityId);
            this.#getInternalStepData(entityId, type, entityType);
        });
        $(`#${this.modal_id} .modal-actions`).off('click', '.next-step')
        .on('click', '.next-step', (e) => {
            const steps = {
                'job-request': 'visit',
                'visit': 'quote',
                'quote': 'job',
                'job': 'invoice',
                'invoice': false,
            };
            this.currentInternalStep
            let next_step = steps[this.currentInternalStep];
            $(`#${this.modal_id} .modal-navigation-internal .internal-step.${next_step}`).click();
            // this.#getInternalStepData(null, 'request', next_step);
        });

        //-Confirmations-//
        $(`body`).off('click', '#confirm-action-modal .confirm-btn');
        $(`#form-container`).off('click', '#confirm-action-modal .confirm-btn')
        .on('click', '#confirm-action-modal .confirm-btn', async (e) => {
            const entity = $(e.currentTarget).data('entity');
            const entity_id = this.parent_entity_id;
            if (entity == 'visit') {
                this.updateStatus('request', entity_id, 'approve', false, () => {
                    this.#getInternalStepData(null, 'request', entity);
                    $('#confirm-action-modal').modal('hide');
                });
            }else if(entity == 'quote') {
                this.#getInternalStepData(null, 'request', entity);
                $('#confirm-action-modal').modal('hide');
            }else if (entity == 'job') {
                $.ajax({
                    method: 'POST',
                    url: `/jobs-flow/request/${entity_id}/convert-to-job`,
                    success: (response) => {
                        const entity_type = 'job';
                        this.#getInternalStepData(null, 'request', entity_type);
                        this.scopeDropZone(entity,entity_id);
                    },
                    error: (response)=>{
                        console.log(response);
                    }
                });
                $('#confirm-action-modal').modal('hide');
            }else if(entity == 'invoice'){
                const entity = $(e.currentTarget).data('entity');
                const entity_id = this.parent_entity_id;
                if (e.currentTarget.hasAttribute('without-quote')) {
                    this.updateStatus('request', entity_id, 'approve', false);
                    this.#getInternalStepData(null, 'request', entity);
                    const type = $('.internal-step.focused').data('step') == 'job-request' ? 'request' : $('.internal-step.focused').data('step');
                    this.scopeDropZone(type,entity_id);
                }
                if(e.currentTarget.hasAttribute('without-job')) {
                    $.ajax({
                        method: 'POST',
                        url: `/jobs-flow/request/${entity_id}/convert-to-job`,
                        success: (response) => {
                            this.#getInternalStepData(null, 'request', entity);
                            const type = $('.internal-step.focused').data('step') == 'job-request' ? 'request' : $('.internal-step.focused').data('step');
                            this.scopeDropZone(type,entity_id);
                        },
                        error: (response)=>{
                            console.log(response);
                        }
                    });
                }
                if (e.currentTarget.hasAttribute('paid_cash')) {
                    const invoice = new Invoice(this.internalEntityId);
                    let result = await invoice.markAsPaidCash();
                    if(result)
                        this.#getInternalStepData(null,'request','invoice');
                }else if (!e.currentTarget.hasAttribute('without-quote') && !e.currentTarget.hasAttribute('without-job')) {
                    this.#getInternalStepData(null, 'request', entity);
                    const type = $('.internal-step.focused').data('step') == 'job-request' ? 'request' : $('.internal-step.focused').data('step');
                    this.scopeDropZone(type,entity_id);
                }

                this.#getInternalStepData(null, 'request', entity);

                $('#confirm-action-modal').modal('hide');
            }

        });
        $('body').on('click', '#confirm-action-modal .cancel-btn', (e)=>{
            const entity_id = $("#customer-projects-modal #selected_request").val();
            if ($(e.currentTarget).data('entity') == 'visit') {
                this.refreshCurrentStep();
                $('#confirm-action-modal').modal('hide');
            }else{
                $('#confirm-action-modal').modal('hide');
            }
        });
    }

    activateAppointmentsListeners(){
        $(`#${this.modal_id} .data-container`).off('click', '.select-appointment-btn')
        .on('click', '.select-appointment-btn', (e) => {
            this.parent_entity_id = $(e.currentTarget).data('id');
            this.parentLocalId = $(e.currentTarget).data('local-id');
            this.internalEntityLocalId = null;
            // Save state to URL when selecting an appointment
            this.saveModalStateToUrl('appointments', 'appointment', this.parent_entity_id);
            this.#getInternalStepData(null, 'appointment', 'appointment');
        });

        // Add filtering functionality
        $(`#${this.modal_id} .data-container`).off('input', '#table_size_select, input[name="date-range"]')
        .on('input', '#table_size_select, input[name="date-range"]', (e)=>{
            if ($(e.currentTarget).attr('name') === 'date-range') {
                // For date range, we need to wait for the apply event
                return;
            }
            this.#getStepData(this.currentStep, this.collectOptionsForStep(), 1000);
        });

        $(`#${this.modal_id} .data-container`).off('change', '#table_size_select, select#status')
        .on('change', '#table_size_select, select#status', (e)=>{
            this.#getStepData(this.currentStep, this.collectOptionsForStep(true));
        });

        // Handle date range apply event
        $(`#${this.modal_id}`).off('apply.daterangepicker', 'input[name="date-range"]')
        .on('apply.daterangepicker', 'input[name="date-range"]', (e, picker) => {
            this.#getStepData(this.currentStep, this.collectOptionsForStep(true));
        });

        // Handle date range cancel event
        $(`#${this.modal_id}`).off('cancel.daterangepicker', 'input[name="date-range"]')
        .on('cancel.daterangepicker', 'input[name="date-range"]', (e, picker) => {
            $(e.currentTarget).val('');
            this.#getStepData(this.currentStep, this.collectOptionsForStep(true));
        });

        // Handle reset filters button
        $(`#${this.modal_id}`).off('click', '.reset-filter-btn')
        .on('click', '.reset-filter-btn', (e) => {
            // Reset all filters
            $(`#${this.modal_id}`).find('input[name="date-range"]').val('');
            $(`#${this.modal_id}`).find('select[name="status"]').val('');
            this.#getStepData(this.currentStep, this.collectOptionsForStep(true));
        });

        // Initialize pagination
        this.initCustomerPagination();

        // Update pagination UI
        setTimeout(() => {
            this.updateCustomerPagination();
        }, 0);
    }

    activateSubscriptionsListeners(){
        $(`#${this.modal_id} .data-container`).off('click', '.select-subscription-btn')
        .on('click', '.select-subscription-btn', (e)=>{
            this.parent_entity_id = $(e.currentTarget).data('id');
            this.parentLocalId = $(e.currentTarget).data('local-id');
            const type = $(e.currentTarget).data('type');
            // Save state to URL when selecting a subscription
            this.saveModalStateToUrl('subscriptions', type, this.parent_entity_id);
            this.#getInternalStepData(null, 'subscription', type);
        });

        // Add filtering functionality
        $(`#${this.modal_id} .data-container`).off('input', '#table_size_select, input[name="date-range"]')
        .on('input', '#table_size_select, input[name="date-range"]', (e)=>{
            if ($(e.currentTarget).attr('name') === 'date-range') {
                // For date range, we need to wait for the apply event
                return;
            }
            this.#getStepData(this.currentStep, this.collectOptionsForStep(), 1000);
        });

        $(`#${this.modal_id} .data-container`).off('change', '#table_size_select, select[name="status"]')
        .on('change', '#table_size_select, select[name="status"]', (e)=>{
            this.#getStepData(this.currentStep, this.collectOptionsForStep(true));
        });

        // Handle date range apply event
        $(`#${this.modal_id}`).off('apply.daterangepicker', 'input[name="date-range"]')
        .on('apply.daterangepicker', 'input[name="date-range"]', (e, picker) => {
            this.#getStepData(this.currentStep, this.collectOptionsForStep(true));
        });

        // Handle date range cancel event
        $(`#${this.modal_id}`).off('cancel.daterangepicker', 'input[name="date-range"]')
        .on('cancel.daterangepicker', 'input[name="date-range"]', (e, picker) => {
            $(e.currentTarget).val('');
            this.#getStepData(this.currentStep, this.collectOptionsForStep(true));
        });

        // Handle reset filters button
        $(`#${this.modal_id}`).off('click', '.reset-filter-btn')
        .on('click', '.reset-filter-btn', (e) => {
            // Reset all filters
            $(`#${this.modal_id}`).find('input[name="date-range"]').val('');
            $(`#${this.modal_id}`).find('select[name="status"]').val('');
            this.#getStepData(this.currentStep, this.collectOptionsForStep(true));
        });

        // Initialize pagination
        this.initCustomerPagination();

        // Update pagination UI
        setTimeout(() => {
            this.updateCustomerPagination();
        }, 0);
    }

    activateOverviewListeners(){
        $(`#${this.modal_id} .internal-data-container`).off('click', '.schedule-container')
            .on('click', '.schedule-container', (e) => {
                const entityId = $(e.currentTarget).data('entity_id');
                const type = $(e.currentTarget).data('type');
                const entityType = $(e.currentTarget).data('entity_type');
                this.internalEntityId = entityId;
                this.internalEntityLocalId = $(e.currentTarget).data('entity_local_id');
                // Save state to URL when selecting a specific entity
                this.saveModalStateToUrl(this.currentStep, entityType, this.parent_entity_id, entityId);
                this.#getInternalStepData(entityId, type, entityType);
            });


        $(`#${this.modal_id} .data-container`).off('input', '#reference-id, #activity-status-select')
        .on('input', '#reference-id, #activity-status-select', (e)=>{
            $(e.currentTarget).parent().find('#search-reset').removeClass('d-none');
            this.#getStepData(this.currentStep, this.collectOptionsForStep(), 1000);
        });
        $(`#${this.modal_id} .data-container`).off('change', '#activity-select, #table_size_select')
        .on('change', '#activity-select, #table_size_select', (e)=>{
            this.#getStepData(this.currentStep, this.collectOptionsForStep(true));
        });
        $(`#${this.modal_id} .data-container`).off('change', '#activity-status-select')
        .on('change', '#activity-status-select', (e)=>{
            $(e.currentTarget).parent().find('#search-reset').removeClass('d-none');
            this.#getStepData(this.currentStep, this.collectOptionsForStep(), 1000);
        });

        $(`#${this.modal_id} .data-container`).off('click', '.select-appointment-btn')
        .on('click', '.select-appointment-btn', (e) => {
            this.parent_entity_id = $(e.currentTarget).data('id');
            this.parentLocalId = $(e.currentTarget).data('local-id');
            this.internalEntityLocalId = null;
            // Save state to URL when selecting an appointment from overview
            this.saveModalStateToUrl('overview', 'appointment', this.parent_entity_id);
            this.#getInternalStepData(null, 'appointment', 'appointment');
        });

        $(`#${this.modal_id} .data-container`).off('click', '.select-subscription-btn')
            .on('click', '.select-subscription-btn', (e) => {
                this.parent_entity_id = $(e.currentTarget).data('id');
                this.parentLocalId = $(e.currentTarget).data('local-id');
                const type = $(e.currentTarget).data('type');
                // Save state to URL when selecting a subscription from overview
                this.saveModalStateToUrl('overview', type, this.parent_entity_id);
                this.#getInternalStepData(null, 'subscription', type);
            });

        $(`#${this.modal_id} .data-container`).off('click', '.select-payment-btn')
            .on('click', '.select-payment-btn', (e) => {
                this.parent_entity_id = $(e.currentTarget).data('id');
                this.parentLocalId = $(e.currentTarget).data('local-id');
                this.internalEntityId = $(e.currentTarget).data('entity_id');
                this.internalEntityLocalId = $(e.currentTarget).data('entity-local-id');
                // Save state to URL when selecting a payment from overview
                this.saveModalStateToUrl(
                    'overview',
                    'payment',
                    this.parent_entity_id,
                    this.internalEntityId
                );
                this.#getInternalStepData(this.internalEntityId, 'appointment', 'payment');
            });

        $(`#${this.modal_id}`).find('.data-container .select-request-btn').off('click')
            .on('click', (e) => {
                this.parent_entity_id = $(e.currentTarget).data('id');
                this.parentLocalId = $(e.currentTarget).data('parent-local-id');
                this.internalEntityLocalId = $(e.currentTarget).data('entity-local-id');
                this.saveModalStateToUrl('projects', null, this.parent_entity_id);
                this.#getInternalStepData($(e.currentTarget).data('entity_id'), 'request', $(e.currentTarget).data('entity-type'));
            });

        // Initialize pagination
        this.initCustomerPagination();

        // Update pagination UI
        setTimeout(() => {
            this.updateCustomerPagination();
        }, 0);

        $(`#${this.modal_id}`).off('click', '#search-reset')
        .on('click','#search-reset', (e)=>{
            $(e.currentTarget).addClass('d-none');
            $(`#${this.modal_id}`).find('#reference-id').val('');
            this.#getStepData(this.currentStep, this.collectOptionsForStep());
        })
    }

    activateInternalNavigationListeners(backNavCallback = null){
        $(`#${this.modal_id}`).off('click', '.modal-navigation-internal .back-btn, .modal-actions .back-btn')
        .on('click', '.modal-navigation-internal .back-btn, .modal-actions .back-btn', (e)=>{
            if (backNavCallback !== null) {
                backNavCallback();
            } else {
                $(`#${this.modal_id}`).find('.data-container').removeClass('d-none');
                $(`#${this.modal_id}`).find('.internal-data-container').addClass('d-none');
                $(`#${this.modal_id}`).find('.modal-navigation-internal').addClass('d-none');
                $(`#${this.modal_id} .modal-actions`).find('button').addClass('d-none');
                $(`#${this.modal_id} .modal-actions`).find('.btn-secondary-close').removeClass('d-none');
            }
        });

        $(`#${this.modal_id} .modal-navigation-internal`).off('click', '.internal-step.active')
        .on('click', '.internal-step.active', (e) => {
            const step = $(e.currentTarget).data('step');
            const type = $(e.currentTarget).data('type');
            // Save internal step to URL
            this.saveModalStateToUrl(this.currentStep, step, this.parent_entity_id);
            this.#getInternalStepData(null, type, step);
        });

        $(`#${this.modal_id} .modal-navigation-internal`).off('click', '.internal-step:not(.active)')
        .on('click', '.internal-step:not(.active)', (e) => {
            const step = $(e.currentTarget).data('step');
            const type = $(e.currentTarget).data('type');
            if (type=='request') {
                this.getConfirmationForm(step, type);
            } else {
                this.getPaymentModal();
            }
        });

        $(`#${this.modal_id} .modal-navigation-internal-mobile`).off('click', '.internal-step')
        .on('click', '.internal-step', (e) => {
            const step = $(e.currentTarget).data('step');
            const type = $(e.currentTarget).data('type');
            this.#getInternalStepData(null, type, step);
        });

        $(`#${this.modal_id} .modal-navigation-internal-mobile`).off('click', '.change-step')
        .on('click', '.change-step', (e) => {
            if (!$(e.currentTarget).hasClass('opened')) {
                $(`#${this.modal_id} .change-step`).removeClass('opened');
                $(e.currentTarget).addClass('opened');
                const next_step = $(e.currentTarget).hasClass('next') ? true : false;
                const current_step = $(`#${this.modal_id} .modal-navigation-internal-mobile .internal-step.focused`);
                const current_index = current_step.parent().index();


                const request_step = $('#customer-projects-modal .project-step.job-request');
                const entities_map = this.getEntitiesMap($(`#${this.modal_id}`).find('.main-step.active').data('type'));
                // new Map([
                //     [1, "job-request"],
                //     [2, "visit"],
                //     [3, "quote"],
                //     [4, "job"],
                //     [5, "invoice"],
                // ]);


                const entities_active_icons_map = new Map([
                    [1,"/images/individual-jobs/steps-icons/request-active-icon.svg"],
                    [2,"/images/individual-jobs/steps-icons/visit-active-icon.svg"],
                    [3,"/images/individual-jobs/steps-icons/estimate-active-icon.svg"],
                    [4,"/images/individual-jobs/steps-icons/job-active-icon.svg"],
                    [5,"/images/individual-jobs/steps-icons/invoice-active-icon.svg"],
                ]);
                const entities_inactive_icons_map = new Map([
                    [1,"/images/individual-jobs/steps-icons/request-inactive-icon.svg"],
                    [2,"/images/individual-jobs/steps-icons/visit-inactive-icon.svg"],
                    [3,"/images/individual-jobs/steps-icons/estimate-inactive-icon.svg"],
                    [4,"/images/individual-jobs/steps-icons/job-inactive-icon.svg"],
                    [5,"/images/individual-jobs/steps-icons/invoice-inactive-icon.svg"],
                ]);
                const enteties_selected_icons_map = new Map([
                    [1,"/images/individual-jobs/steps-icons/request-selected-icon.svg"],
                    [2,"/images/individual-jobs/steps-icons/visit-selected-icon.svg"],
                    [3,"/images/individual-jobs/steps-icons/estimate-selected-icon.svg"],
                    [4,"/images/individual-jobs/steps-icons/job-selected-icon.svg"],
                    [5,"/images/individual-jobs/steps-icons/invoice-selected-icon.svg"],
                ]);
                $(`#${this.modal_id} .step-list`).html('');

                if (next_step) {
                    for (let i = current_index+2; i <= entities_map.size; i++) {
                        let step_name = entities_map.get(i).replace('_',' ');
                        step_name = this.ucFirst(step_name);
                        let icon = entities_active_icons_map.get(i);
                        $(`#${this.modal_id} .step-list`).append(`<div class="step-list-row" data-step="${entities_map.get(i)}"><img src="${icon}"><div>${step_name}</div></div>`)
                    }
                    let posX = 5;
                    let posY = 40;
                    $(`#${this.modal_id} .step-list`).removeAttr('style').css({right: posX, top: posY});
                    if ($(`#${this.modal_id} .step-list`).html() !=='') {
                        $(`#${this.modal_id} .step-list`).removeClass('d-none');
                    }
                } else {
                    for (let j = current_index; j > 0; j--) {
                        let step_name = entities_map.get(j).replace('-',' ');
                        step_name = this.ucFirst(step_name);
                        let icon = entities_active_icons_map.get(j);
                        $(`#${this.modal_id} .step-list`).append(`<div class="step-list-row" data-step="${entities_map.get(j)}"><img src="${icon}"><div>${step_name}</div></div>`)
                    }
                    let posX = 5;
                    let posY = 40;
                    $(`#${this.modal_id} .step-list`).removeAttr('style').css({left: posX, top: posY});
                    if ($(`#${this.modal_id} .step-list`).html() !=='') {
                        $(`#${this.modal_id} .step-list`).removeClass('d-none');
                    }else{
                        $(`#${this.modal_id} .step-list`).addClass('d-none');
                    }
                }
            } else {
                $(e.currentTarget).removeClass('opened');
                $(`#${this.modal_id} .change-step.opened`).removeClass('opened');
                $(`#${this.modal_id} .step-list`).addClass('d-none');
            }
        });

        $(`#${this.modal_id}`).off('click', '.step-list .step-list-row')
        .on('click','.step-list .step-list-row', (e) => {
            $(`#${this.modal_id} .step-list`).addClass('d-none');
            $(`#${this.modal_id} .change-step.opened`).removeClass('opened');
            $(`#${this.modal_id} .modal-navigation-internal .internal-step.${$(e.currentTarget).data('step')}`).click();
        });
    }

    getEntitiesMap(type){
        switch (type) {
            case 'projects':
                return new Map([
                    [1, "job-request"],
                    [2, "visit"],
                    [3, "quote"],
                    [4, "job"],
                    [5, "invoice"],
                ]);
                break;
            case 'appointments':
                return new Map([
                    [1, "appointment"],
                    [2, "payment"],
                ]);
                break;
            default:
                return [];
                break;
        }
    }

    initDataInput(reset_value = true){
        daterangepicker.setPicker(
            $(`#${this.modal_id}`).find('.data-container .daterange-input'),
            'MM/DD/YYYY',
            true,
            false,
            true
        );

        if (reset_value) {
            $(`#${this.modal_id}`).find('.data-container .daterange-input').val('');
        }
        $(`#${this.modal_id}`).off('click','.data-container .daterange-input').on('click','.data-container .daterange-input', (e)=>{
            $('.daterange-input').off('cancel.daterangepicker').on('cancel.daterangepicker', (e,p)=>{
                $(e.currentTarget).val('');
                this.#getStepData(this.currentStep, this.collectOptionsForStep());
            });
            $('.daterange-input').off('apply.daterangepicker').on('apply.daterangepicker', (e,p)=>{
                this.#getStepData(this.currentStep, this.collectOptionsForStep());
            });
        });
    }

    initStatusSelect(){
        $(`#${this.modal_id} .info-container`).off('change','select#status')
        .on('change', 'select#status', (e)=>{
            this.#getStepData(this.currentStep, this.collectOptionsForStep());
        });
    }

    collectOptionsForStep(without_status=false, step = 1){
        let options = {};

        let param_fields = $(`#${this.modal_id} .data-container`).find('select, input').toArray();
        param_fields.forEach(element => {
            if ($(element).attr('name') == 'status' && ($(`#${this.modal_id} .data-container`).find('#activity-select').val() =='' || without_status)) {
                options[$(element).attr('name')] = '';
            }else{
                options[$(element).attr('name')] = $(element).val();
            }
        });
        options['page_number'] = step;
        return options;
    }

    getConfirmationForm(entity, type, sub_action = null){
        if (type=='request') {
            $.ajax({
                method:'POST',
                url: '/jobs-flow/get-confirmation-modal',
                data: {
                    entity: entity,
                    job_request_id: this.parent_entity_id,
                    sub_action: sub_action
                },
                success: (response) =>{
                    if (response.need_confirm) {
                        $('#form-container').html(response.view);
                        $('#confirm-action-modal').modal('show');
                    } else {
                        this.#getInternalStepData(null, type, entity);
                    }
                },
                error: (r) => {
                    console.log(r);
                }
            });
        }
    }

    getPaymentModal(type='charge'){
        if (type == 'charge') {
            $.ajax({
                url: '/get-payment-modal/' + this.parentLocalId,
                method: 'post',
                success: (response) => {
                    $('#form-container').html(response);
                    $(`#payments-appointments-${this.parentLocalId}`).modal('show');
                },
            });
        } else if(type == 'manual') {
            let id = this.internalEntityId ?? this.parent_entity_id
            $.ajax({
                url: '/payments/' + id + '/manual-charge',
                method: 'get',
                success: function (response) {
                    $('.manual-charge-modal').remove();
                    $('#form-container').html(response);
                    initMoneyMask();
                    $('.manual-charge-modal').modal('show');

                    $('#additional_input').on('input', function () {
                        const value = $(this).val().trim();
                        const button = $('.charge-btn');

                        if (value !== '') {
                            button.prop('disabled', false);
                        } else {
                            button.prop('disabled', true);
                        }
                    });

                    $(document).on('click', '.charge-btn', function () {
                        if (!$(this).prop('disabled')) {
                            $('.manual-charge-modal').modal('hide');
                        }
                    });
                },
            });
        }
    }

    getManualChargeModal(order_id) {
        $.ajax({
            url: '/payments/' + order_id + '/manual-charge',
            method: 'get',
            success: function (data) {
                $('.manual-charge-modal').remove();
                $('#form-container').append(data);
                initMoneyMask();
                $('.manual-charge-modal').modal('show');

                $('#additional_input').on('input', function () {
                    const value = $(this).val().trim();
                    const button = $('.charge-btn');

                    if (value !== '') {
                        button.prop('disabled', false);
                    } else {
                        button.prop('disabled', true);
                    }
                });
            },
        });
    }

    activateInternalStepActions(step){
        // Common handler for refund-deposit-btn (works for both invoice and quote modals)
        $(`#${this.modal_id} .internal-data-container`).off('click', '.refund-deposit-btn')
        .on('click', '.refund-deposit-btn', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const paymentId = $(e.currentTarget).data('payment-id');
            const invoiceId = $(e.currentTarget).data('invoice-id');
            const depositAmount = parseFloat($(e.currentTarget).data('deposit-amount')) || 0;
            const invoiceTotal = parseFloat($(e.currentTarget).data('invoice-total')) || 0;
            
            // Load confirmation modal using invoice ID (works for both invoice and quote modals)
            try {
                const response = await fetch(`/jobs-flow/form?entity=invoice&type=refund-deposit&id=${invoiceId}&form-request=true`, {
                    method: 'GET',
                    headers: {
                        'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content')
                    }
                });
                
                const html = await response.text();
                
                // Remove existing confirmation modal if present
                $('#confirm-action-modal').remove();
                
                // Append modal to form-container
                $('#form-container').append(html);
                
                const confirmModal = $('#confirm-action-modal');
                
                // Show modal
                confirmModal.modal('show');
                
                // Handle confirmation button click
                const self = this;
                const currentStep = self.currentInternalStep; // Save current step to refresh correct entity
                confirmModal.off('click', '.confirm-btn').on('click', '.confirm-btn', async function() {
                    confirmModal.modal('hide');
                    
                    try {
                        // Send refund request
                        const refundResponse = await fetch(`/jobs-flow/invoice/${invoiceId}/refund`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content')
                            },
                            body: JSON.stringify({
                                max: "" + invoiceTotal + "",
                                amount: "" + depositAmount + "",
                                reason: 'fraudulent'
                            })
                        });
                        
                        const refundData = await refundResponse.json();
                        
                        if (refundData.success) {
                            // Remove existing success modal if present
                            $('#success_refund-modal').remove();
                            
                            // Append success modal to form-container
                            $('#form-container').append(refundData.modal);
                            
                            // Show success modal
                            $('#success_refund-modal').modal('show');
                            
                            // Refresh data after 2 seconds - refresh based on current step (quote or invoice)
                            setTimeout(() => {
                                self.#getInternalStepData(null, 'request', currentStep);
                            }, 2000);
                        } else {
                            // Handle error - show modal with error message
                            const errorMessage = refundData.reason || refundData.error || 'Failed to refund deposit';
                            console.error('Refund failed:', errorMessage);
                            
                            // Remove existing failed modal if present
                            $('#failed_refund-modal').remove();
                            
                            // Create failed modal
                            const failedModalHtml = `
                                <div class="modal fade modal-form verify" id="failed_refund-modal" tabindex="-1" aria-labelledby="failed_refund-modal-title" aria-hidden="true">
                                    <div class="modal-dialog modal-lg" role="dialog">
                                        <div class="modal-content">
                                            <div class="modal-header d-flex flex-column justify-content-center m-auto">
                                                <div class="d-flex justify-content-center mx-auto mt-2">
                                                    <div class="icon d-flex justify-content-center mr-0 error_circle">
                                                        <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <circle cx="30" cy="30" r="29" fill="white" stroke="#DB3A57" stroke-width="2"></circle>
                                                            <line x1="20" y1="20" x2="40" y2="40" stroke="#DB3A57" stroke-width="2"></line>
                                                            <line x1="40" y1="20" x2="20" y2="40" stroke="#DB3A57" stroke-width="2"></line>
                                                        </svg>
                                                    </div>
                                                </div>
                                                <div class="text-center modal-title mt-3">Refund Failed</div>
                                                <div class="close-btn d-flex justify-content-end">
                                                    <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                                                        <span aria-hidden="true"><i class="fa fa-lg fa-close"></i></span>
                                                    </button>
                                                </div>
                                            </div>
                                            <div class="modal-body">
                                                <div class="form-validation-errors"></div>
                                                <div class="mt-3 ml-3 mr-3">
                                                    <form id="failed_refund">
                                                        <div class="message mt-n3 mb-3" style="font-size: 15px;line-height: 18px;text-align: center">
                                                            <span class="error-message">${errorMessage}</span>
                                                        </div>
                                                        <div class="modal-info text-center text-muted mt-2">You may now close this page.</div>
                                                        <div class="form-group d-flex pt-4 mb-0">
                                                            <button type="button" class="btn btn-outline-primary w-100" data-dismiss="modal" aria-label="Close">Close</button>
                                                        </div>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            `;
                            $('#form-container').append(failedModalHtml);
                            $('#failed_refund-modal').modal('show');
                        }
                    } catch (error) {
                        console.error('Error refunding deposit:', error);
                        
                        // Remove existing failed modal if present
                        $('#failed_refund-modal').remove();
                        
                        // Create failed modal
                        const failedModalHtml = `
                            <div class="modal fade modal-form verify" id="failed_refund-modal" tabindex="-1" aria-labelledby="failed_refund-modal-title" aria-hidden="true">
                                <div class="modal-dialog modal-lg" role="dialog">
                                    <div class="modal-content">
                                        <div class="modal-header d-flex flex-column justify-content-center m-auto">
                                            <div class="d-flex justify-content-center mx-auto mt-2">
                                                <div class="icon d-flex justify-content-center mr-0 error_circle">
                                                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <circle cx="30" cy="30" r="29" fill="white" stroke="#DB3A57" stroke-width="2"></circle>
                                                        <line x1="20" y1="20" x2="40" y2="40" stroke="#DB3A57" stroke-width="2"></line>
                                                        <line x1="40" y1="20" x2="20" y2="40" stroke="#DB3A57" stroke-width="2"></line>
                                                    </svg>
                                                </div>
                                            </div>
                                            <div class="text-center modal-title mt-3">Refund Failed</div>
                                            <div class="close-btn d-flex justify-content-end">
                                                <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                                                    <span aria-hidden="true"><i class="fa fa-lg fa-close"></i></span>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="modal-body">
                                            <div class="form-validation-errors"></div>
                                            <div class="mt-3 ml-3 mr-3">
                                                <form id="failed_refund">
                                                    <div class="message mt-n3 mb-3" style="font-size: 15px;line-height: 18px;text-align: center">
                                                        Failed to refund deposit. Please try again.
                                                    </div>
                                                    <div class="modal-info text-center text-muted mt-2">You may now close this page.</div>
                                                    <div class="form-group d-flex pt-4 mb-0">
                                                        <button type="button" class="btn btn-outline-primary w-100" data-dismiss="modal" aria-label="Close">Close</button>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        `;
                        $('#form-container').append(failedModalHtml);
                        $('#failed_refund-modal').modal('show');
                    }
                });
            } catch (error) {
                console.error('Error loading confirmation modal:', error);
                alert('Error loading confirmation. Please try again.');
            }
        });
        
        switch (step) {
            case 'job-request':
                $(`#${this.modal_id} .internal-data-container`).off('click', '.edit-request-details')
                .on('click', '.edit-request-details', (e) => {
                    if (!$(e.currentTarget).hasClass('editing')) {
                        this.hidedSelectBehavior();
                        $(e.currentTarget).addClass('editing');
                        $(e.currentTarget).find('div').text('Editing');
                        $(`#${this.modal_id} .internal-data-container`).find('textarea').removeAttr('disabled');
                        $(`#${this.modal_id} .modal-actions`).find('.btn').addClass('d-none');
                        $(`#${this.modal_id} button.save-request-changes`).removeClass('d-none');
                        $(`#${this.modal_id} button.cancel-editing`).removeClass('d-none');
                        $(`#${this.modal_id} .internal-data-container`).find('.status-select').addClass('editing');

                        $(`#${this.modal_id} .internal-data-container`).find('.request-description-row').find('.request-description-row-value:not(.static)').addClass('d-none');
                        $(`#${this.modal_id} .internal-data-container`).find('.request-description-row').find('.hided-select-container').removeClass('d-none');
                    }
                });

                $(`#${this.modal_id} .modal-actions`).off('click', '.save-request-changes')
                .on('click', '.save-request-changes', (e) => {
                    this.showModalLoader();
                    $(`#${this.modal_id} .modal-actions`).find('.btn').attr('disabled', true);
                    const request = new JobRequest(this.parent_entity_id);

                    request.update(()=>{
                        this.#getInternalStepData(null,'request','job-request');
                        this.hideModalLoader();
                    });
                });
                $(`#${this.modal_id} .modal-actions`).off('click', '.cancel-editing')
                .on('click', '.cancel-editing', (e)=> {
                    const step = $(`#${this.modal_id}`).find('.internal-step.focused').data('step');
                    this.#getInternalStepData(null,'request',step);
                });
                break;
            case 'visit':
                $(`#${this.modal_id} .internal-data-container`).off('click', '.create-visit-btn')
                .on('click', '.create-visit-btn', (e)=>{
                    $.ajax({
                        method: 'POST',
                        url: '/jobs-flow/create-entity-form',
                        data: {
                            entity: 'visit',
                            job_request_id: $(e.target).data('job_request_id'),
                        },
                        success: (response) => {
                            $(`#${this.modal_id} .internal-data-container .entity-info-container`).html(response);
                            $(`#${this.modal_id} .internal-data-container #entity_id`).val('');
                            $(`#${this.modal_id} .modal-actions`).find('.cancel-visit-btn').removeClass('d-none');
                            $(`#${this.modal_id} .modal-actions`).find('.save-visit-btn').removeClass('d-none');
                            $(`#${this.modal_id} .modal-actions`).find('.next-step').addClass('d-none');
                            $(`#${this.modal_id}`).find('#visit_preffered_date').datetimepicker({
                                format: 'ddd, MMM D',
                                defaultDate: new Date(),
                            });
                            $(`#${this.modal_id}`).find('#visit_preffered_date').on('dp.change',(e) => {
                                let picker = $(e.target).data('DateTimePicker');
                                let date_input = $(e.target).parent().find('input[name="date"]');
                                date_input.val(picker.date().format('Y-M-D'));
                            });
                        },
                        error: (response) => {

                        }
                    });
                });

                $(`#${this.modal_id} .internal-data-container`).off('click', '.edit-visit-btn')
                .on('click', '.edit-visit-btn', (e) => {
                    $.ajax({
                        method: 'POST',
                        url: '/jobs-flow/create-entity-form',
                        data: {
                            entity: 'visit',
                            job_request_id: this.parent_entity_id,
                            entity_id: $(e.currentTarget).data('entity_id'),
                        },
                        success: (response) => {
                            $(`#${this.modal_id} .internal-data-container .entity-info-container`).html(response);
                            $(`#${this.modal_id} .modal-actions`).find('.cancel-visit-btn').removeClass('d-none');
                            $(`#${this.modal_id} .modal-actions`).find('.save-visit-btn').removeClass('d-none');
                            $(`#${this.modal_id} .modal-actions`).find('.next-step').addClass('d-none');
                            $(`#${this.modal_id}`).find('#visit_preffered_date').datetimepicker({
                                format: 'ddd, MMM D, Y',
                                defaultDate: new Date(),
                            });
                            $(`#${this.modal_id}`).find('#visit_preffered_date').on('dp.change',(e) => {
                                let picker = $(e.target).data('DateTimePicker');
                                let date_input = $(e.target).parent().find('input[name="date"]');
                                date_input.val(picker.date().format('Y-M-D'));
                            });
                        },
                        error: (response) => {

                        }
                    });
                });
                $(`#${this.modal_id} .modal-actions`).off('click', '.cancel-visit-btn')
                .on('click', '.cancel-visit-btn', (e) => {
                    this.#getInternalStepData(null,'request','visit');
                });
                $(`#${this.modal_id} .modal-actions`).off('click', '.save-visit-btn')
                .on('click', '.save-visit-btn', (e)=> {
                    this.showModalLoader();
                    $(`#${this.modal_id} .modal-actions`).find('.btn').attr('disabled', true);
                    let entity_id = $('.internal-data-container #entity_id').val();
                    if ($('.internal-data-container #entity_id').val()>0) {
                        let visit = new Visit($('.internal-data-container #entity_id').val());
                        visit.update(()=>{
                            setTimeout(() => {
                                this.#getInternalStepData(entity_id,'request','visit');
                                //this.scopeDropZone(type,entity_id);
                            }, 2000);
                        }, this.errorCallback);
                    }else{
                        let visit = new Visit();
                        visit.create(()=>{
                            setTimeout(() => {
                                this.#getInternalStepData(entity_id,'request','visit');
                                //this.scopeDropZone(type,entity_id);
                            }, 2000);
                        }, this.errorCallback);
                    }
                })
                this.workerSelectBehavior();
                break;
            case 'quote':
                $(`#${this.modal_id}`).off('click', '.create-quote-btn')
                .on('click', '.create-quote-btn', (e) => {
                    if ($(e.currentTarget).hasClass('disabled')) {
                        return;
                    }
                    const requestID = $(e.currentTarget).data('request_id');
                    this.openLinkInNewTab(`/jobs-flow/quote/create?job_request_id=${requestID}`, true);
                    $(e.currentTarget).addClass('disabled');
                    this.onFocusEvent(null,'quote');
                });
                $(`#${this.modal_id} .internal-data-container`).off('click', '.edit-quote-btn')
                .on('click', '.edit-quote-btn', (e) => {
                    let id = $(e.currentTarget).data('id');
                    this.openLinkInNewTab(`/jobs-flow/quote/${id}/edit`, true);
                });
                $(`#${this.modal_id} .internal-data-container`).off('click', '.quote-download-pdf')
                .on('click', '.quote-download-pdf', (e) => {
                    let id = $(e.currentTarget).data('id');
                    this.openLinkInNewTab(`/jobs-flow/quote/${id}/pdf`, true);
                });
                $(`#${this.modal_id} .internal-data-container`).off('click', '.show-item-info')
                .on('click', '.show-item-info', (e)=>{                    
                    const item_id = $(e.currentTarget).data('id');
                    if (Number.isInteger(item_id)) {
                        $.ajax({
                            method: 'GET',
                            url: '/jobs-flow/form',
                            data: {
                                id: item_id,
                                entity: 'item',
                                type: 'view',
                                'form-request': true
                            },
                            success:(r)=>{
                                $('#form-container').html(r);
                                $('#view-item-modal').modal('show');
                            },
                            error:(r)=>{

                            }
                        });
                    }
                });
                this.sendEntityFromSms();
                break;
            case 'job':
                $(`#${this.modal_id} .internal-data-container`).off('click','.edit-job-details')
                .on('click', '.edit-job-details', (e) => {
                    if (!$(e.currentTarget).hasClass('editing') && !$(e.currentTarget).hasClass('creating')) {
                        this.hidedSelectBehavior();
                        this.workerSelectBehavior();
                        $(e.currentTarget).addClass('editing');
                        $(e.currentTarget).find('div').text('Editing');
                        $(`#${this.modal_id} .job-details-container`).find('textarea').removeAttr('disabled');
                        $(`#${this.modal_id} .modal-actions button.save-job-details`).removeClass('d-none');
                        $(`#${this.modal_id} .modal-actions button.cancel-editing`).removeClass('d-none');
                        $(`#${this.modal_id} .modal-actions button.next-step`).addClass('d-none');

                        $(`#${this.modal_id} .job-details-container`).find('.job-description-row').find('.job-description-row-value:not(.static)').removeClass('d-flex').addClass('d-none');
                        $(`#${this.modal_id} .job-details-container`).find('.job-description-row').find('.hided-select-container').removeClass('d-none');
                        $(`#${this.modal_id} .job-details-container`).find('.hided-select-container').find('.save-job-workers').addClass('d-none');
                    }
                });
                $(`#${this.modal_id}`).off('click', '.save-job-details')
                .on('click', '.save-job-details', (e) => {
                    this.showModalLoader();
                    const job_id = $(`#${this.modal_id} .internal-data-container`).find('#entity_id').val();
                    const job = new Job(job_id);
                    job.update(()=>{
                        this.#getInternalStepData(null,'request','job');
                        this.hideModalLoader();
                    });
                });
                // .edit-job-workers / .save-job-workers removed 2026-08-05 (Cutover Pilot).
                // A job's Workers row grew its own inline multi-select and Save button — a second
                // way to do what the appointment already did through the shared Assign workers
                // modal. Both views now call getJobWorkersModal(), so no markup carries these
                // classes and these handlers could never fire again.
               $(`#${this.modal_id}`).off('click', '.create-job-btn')
               .on('click', '.create-job-btn', (e) => {
                    const requestId = $(`#${this.modal_id} .internal-data-container #request_id`).val();
                   $.ajax({
                       method: 'POST',
                        url: '/jobs-flow/request/' + requestId + '/convert-to-job',
                        success: (response) => {
                            this.#getInternalStepData(null,'request','job');
                        },
                        error: (response)=>{
                            const message = response.responseJSON?.message
                                ?? `Unable to create job (HTTP ${response.status}).`;
                            $('#failed-action-modal .message').text(message);
                            $('#failed-action-modal').modal('show');
                            console.error('Unable to create job:', message);
                        }
                    });
                });
                this.scopeDropZone('job', $(`#${this.modal_id} .internal-data-container`).find('#entity_id').val());
                break;
            case 'invoice':
                $(`#${this.modal_id} .internal-data-container`).off('click', '.create-invoice-btn')
                .on('click', '.create-invoice-btn', (e) => {
                    if ($(e.currentTarget).hasClass('disabled')) {
                        return;
                    }
                    const requestID = $(e.currentTarget).data('request_id');
                    this.openLinkInNewTab(`/jobs-flow/invoice/create?job_request_id=${requestID}`, true);
                    $(e.currentTarget).addClass('disabled');
                    this.onFocusEvent(null,'invoice');
                });
                $(`#${this.modal_id} .internal-data-container`).off('click', '.invoice-download-pdf')
                .on('click', '.invoice-download-pdf', (e) => {
                    let id = $(e.currentTarget).data('id');
                    this.openLinkInNewTab(`/jobs-flow/invoice/${id}/pdf`, true);
                });
                $(`#${this.modal_id} .internal-data-container`).off('click', '.invoice-payment-info-btn')
                .on('click', '.invoice-payment-info-btn', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    let id = $(e.currentTarget).data('id');
                    const parentModalId = this.modal_id;
                    const parentModal = $(`#${parentModalId}`);
                    
                    // Prevent parent modal from closing - attach handler immediately
                    const preventCloseHandler = function(e) {
                        // Only prevent if payment modal exists and is not yet shown
                        const paymentModal = $('#payment-info-invoice-modal');
                        if (paymentModal.length && !paymentModal.hasClass('show')) {
                            e.preventDefault();
                            e.stopImmediatePropagation();
                            return false;
                        }
                    };
                    
                    // Attach handler to parent modal BEFORE any other operations
                    parentModal.on('hide.bs.modal', preventCloseHandler);
                    
                    $.ajax({
                        type: 'GET',
                        url: `/jobs-flow/form?entity=invoice&type=payment-info&id=${id}&form-request=true`,
                        success: (response) => {
                            // Remove existing payment info modal if any
                            $('#payment-info-invoice-modal').remove();
                            // Append to form-container (not form-container-s) to keep parent modal
                            $('#form-container').append(response);
                            
                            const paymentModal = $('#payment-info-invoice-modal');
                            
                            // Store parent modal's current state
                            const parentZIndex = parseInt(parentModal.css('z-index')) || 1050;
                            const parentWasShown = parentModal.hasClass('show');
                            
                            // Set z-index for payment modal to appear above parent
                            paymentModal.css('z-index', parentZIndex + 10);
                            
                            // Show payment info modal WITHOUT backdrop (use parent's backdrop)
                            paymentModal.attr('data-backdrop', 'false');
                            paymentModal.modal({
                                backdrop: false,
                                keyboard: true
                            });
                            
                            // Immediately after starting to show, ensure parent stays visible
                            paymentModal.on('show.bs.modal', function() {
                                // Keep parent modal visible
                                if (parentWasShown) {
                                    parentModal.addClass('show').css({
                                        'display': 'block',
                                        'z-index': parentZIndex
                                    });
                                    $('body').addClass('modal-open');
                                }
                            });
                            
                            // After payment modal is fully shown, ensure parent stays visible
                            paymentModal.one('shown.bs.modal', function() {
                                // Force parent modal to stay visible
                                if (parentWasShown) {
                                    parentModal.addClass('show').css({
                                        'display': 'block',
                                        'z-index': parentZIndex
                                    });
                                    $('body').addClass('modal-open');
                                }
                                
                                // Ensure payment modal has higher z-index
                                paymentModal.css('z-index', parentZIndex + 10);
                            });
                            
                            // When payment modal closes, ensure parent stays open
                            paymentModal.off('hidden.bs.modal').on('hidden.bs.modal', function() {
                                // Ensure parent modal is still visible
                                if (parentWasShown) {
                                    parentModal.addClass('show').css({
                                        'display': 'block',
                                        'z-index': parentZIndex
                                    });
                                    $('body').addClass('modal-open');
                                }
                                
                                // Remove the prevent close handler
                                parentModal.off('hide.bs.modal', preventCloseHandler);
                            });
                        },
                        error: (err) => {
                            console.error('Error loading payment info:', err);
                            // Remove handler on error
                            parentModal.off('hide.bs.modal', preventCloseHandler);
                        }
                    });
                });
                $(`#${this.modal_id} .internal-data-container`).off('click', '.edit-invoice-btn')
                .on('click', '.edit-invoice-btn', (e) => {
                    let id = $(e.currentTarget).data('id');
                    this.openLinkInNewTab(`/jobs-flow/invoice/${id}/edit`, true);
                    this.onFocusEvent(id,'invoice');
                });
                $(`#${this.modal_id} .internal-data-container`).off('click', '.exit-preview-invoice-btn')
                .on('click', '.exit-preview-invoice-btn', (e) => {
                    let id = $(e.currentTarget).data('entity_id');
                    this.#getInternalStepData(null,'request','invoice');
                });
                $(`#${this.modal_id} .internal-data-container`).off('click', '.refund-invoice-btn')
                .on('click', '.refund-invoice-btn', (e) => {
                    let id = $(e.currentTarget).data('entity_id');
                    const invoice = new Invoice(id);
                    $('body').one('hidden.bs.modal', '#success_refund-modal', () => {
                        this.#getInternalStepData(id, 'request', 'invoice');
                    });
                    invoice.refundForm();
                });
                $(`#${this.modal_id} .internal-data-container`).off('click', '.paid-cash-invoice-btn')
                .on('click', '.paid-cash-invoice-btn', async (e) => {
                    this.internalEntityId = $(e.currentTarget).data('entity_id');
                    this.getConfirmationForm('invoice', 'request', 'paid-cash');
                    // const invoice = new Invoice(id);
                    // let result = await invoice.markAsPaidCash();
                    // if(result)
                    //     this.#getInternalStepData(null,'request','invoice');
                });
                $(`#${this.modal_id} .internal-data-container`).off('click', '.show-item-info')
                .on('click', '.show-item-info', (e)=>{                    
                    const item_id = $(e.currentTarget).data('id');
                    if (Number.isInteger(item_id)) {
                        $.ajax({
                            method: 'GET',
                            url: '/jobs-flow/form',
                            data: {
                                id: item_id,
                                entity: 'item',
                                type: 'view',
                                'form-request': true
                            },
                            success:(r)=>{
                                $('#form-container').html(r);
                                $('#view-item-modal').modal('show');
                            },
                            error:(r)=>{

                            }
                        });
                    }
                });
                this.sendEntityFromSms();
                break;
            case 'payment':
                $(`#${this.modal_id} .internal-data-container`).off('click', '.manual-charge-btn')
                .on('click', '.manual-charge-btn', (e)=>{
                    this.internalEntityId = $('.manual-charge-btn').data('entity_id')
                    this.getPaymentModal('manual');
                });
            break;
            case 'appointment':
                $(`#${this.modal_id}`).off('click', '.edit-appointment-info-btn')
                .on('click', '.edit-appointment-info-btn', ()=>{
                    $('#leave-comments-modal .comment-button').data('type', 'order');
                    $('#leave-comments-modal .comment-button').data('id', this.parent_entity_id);
                    $('#leave-comments-modal').modal('show');
                });
                $(`#${this.modal_id}`).off('click', '.edit-appointment-duration-btn')
                .on('click', '.edit-appointment-duration-btn', (e)=>{
                    this.openAppointmentDurationModal($(e.currentTarget).data('appointment-id'));
                });
                $(`#${this.modal_id}`).off('click', '.is-subscription-indicator')
                .on('click', '.is-subscription-indicator', (e)=>{
                    const appt_id = this.parent_entity_id;
                    const apptLocalId = this.parentLocalId;

                    this.parent_entity_id = $(e.currentTarget).data('id');
                    this.parentLocalId = $(e.currentTarget).data('local-id');
                    this.currentStep = 'subscriptions';
                    const type = $(e.currentTarget).data('type');
                    $('.main-step.active').removeClass('active');
                    $('.main-step.active').removeClass('active');
                    $('.main-step[data-type="subscriptions"]').addClass('active');
                    //this.#getStepData('subscriptions',{}, 0, ()=>{
                        this.#getInternalStepData(null, 'subscription', type, ()=>{}, ()=>{}, ()=>{
                            $('.main-step.active').removeClass('active');
                            $('.main-step[data-type="appointments"]').addClass('active');
                            this.parent_entity_id = appt_id;
                            this.parentLocalId = apptLocalId;
                            this.currentStep = 'appointments';

                            this.#getInternalStepData(null, 'appointment', 'appointment');
                        });
                    //});
                });
                this.scopeDropZone('order',this.parent_entity_id);
            break;
            default:
                break;
        }
    }

    openAppointmentDurationModal(appointmentId){
        $.ajax({
            method: 'GET',
            url: `/customers/${this.customer_id}/appointment/${appointmentId}/duration-modal`,
            success: (response)=>{
                $('#appointment-duration-modal').remove();
                const $container = $('#form-container').length ? $('#form-container') : $('body');
                $container.append(response);

                const $modal = $('#appointment-duration-modal');
                $modal.modal('show');
                $modal.find('#appointment-duration-form').off('submit').on('submit', (e)=>{
                    e.preventDefault();
                    this.updateAppointmentDuration(appointmentId, $modal);
                });
            },
            error: (response)=>{
                console.error('Unable to load appointment duration modal', response);
            }
        });
    }

    updateAppointmentDuration(appointmentId, $modal){
        const $form = $modal.find('#appointment-duration-form');
        const $submit = $form.find('[type="submit"]');
        const $errors = $modal.find('.form-validation-errors');

        $submit.prop('disabled', true);
        $errors.empty();

        $.ajax({
            method: 'POST',
            url: `/customers/${this.customer_id}/appointment/${appointmentId}/duration`,
            data: $form.serialize(),
            success: ()=>{
                $modal.one('hidden.bs.modal', ()=>{
                    $modal.remove();
                    this.refreshCurrentStep();
                });
                $modal.modal('hide');
            },
            error: (response)=>{
                const errors = response.responseJSON?.errors;
                const messages = errors
                    ? Object.values(errors).flat()
                    : [response.responseJSON?.message || 'Unable to update appointment duration.'];
                $errors.text(messages.join(' '));
                $submit.prop('disabled', false);
            }
        });
    }

    openLinkInNewTab(link, focus=true){
        window.open(link, (focus==true ? '_blank' : ''));
    }

    onFocusEvent(entity_id, entity){
        window.onfocus = () =>{
            this.#getInternalStepData(entity_id, 'request', entity)
            window.onfocus = () => {};
        }
    }

    changeStatus(){
        $('body').off('click', '.status-select .status-select-option'); //удалить по завершению модуля
        $(`#${this.modal_id} .internal-data-container`).off('click', '.status-select')
        .on('click', '.status-select', function(e) {
            const options_container = $(this).find('.status-select-options-container');
            if (options_container.hasClass('d-none')) {
                options_container.removeClass('d-none');
            } else {
                options_container.addClass('d-none');
            }
            let posX = 0;
            let posY = 30;
            options_container.css({left: posX, top: posY});
        });
        $(`#${this.modal_id} .internal-data-container`).off('click', '.status-select .status-select-option')
        .on('click', '.status-select .status-select-option', (e)=>{
            const parent = $(e.currentTarget).parents('.status-select');
            const active_section = $(`#${this.modal_id}`).find('.main-step.active').data('type');
            if ($(parent).hasClass('editing')) {
                $(parent).find('.selected-value').html($(e.currentTarget).html());
                // $(parent).find('#status').val($(e.currentTarget).data('id'));
            } else {
                const entity = $(e.currentTarget).parents('.status-select').data('entity');
                const entity_id = $(e.currentTarget).parents('.status-select').data('entity_id');
                let status_id = null;
                if (active_section == 'projects') {
                    status_id = $(e.currentTarget).data('name');
                } else {
                    status_id = $(e.currentTarget).data('id');
                }
                if (!entity || !entity_id || !status_id) {
                    return;
                }
                this.updateStatus(entity,entity_id,status_id);
            }
        });
    }

    updateStatus(entity, entity_id, status_id, with_refresh=true, onSuccess=null){
        const active_section = $(`#${this.modal_id}`).find('.main-step.active').data('type');
        this.showModalLoader();
        if (active_section == 'projects') {
            $.ajax({
                method: 'POST',
                url: `/jobs-flow/${entity}/${entity_id}/${status_id}`,
                success: (response) => {
                    if (with_refresh) {
                        const next_step = (entity == 'request') ? 'job-request' : entity;
                        let job_request_id = (entity == 'request') ? entity_id : $(parent).data('job_request_id');
                        if(!job_request_id)
                            job_request_id = $('#selected_request').val()

                        this.#getInternalStepData(null,'request',next_step);
                    } else {
                        this.hideModalLoader();
                    }
                    if (onSuccess) {
                        onSuccess(response);
                    }
                },
                error: (response) => {
                    console.log(response);
                    this.hideModalLoader();
                }
            });
        } else {
            $.ajax({
                method: 'POST',
                url: `order/${entity_id}/change-status`,
                data: {
                    status: status_id
                },
                success: (response)=>{
                    if (with_refresh) {
                        this.#getInternalStepData(null,'appointment','appointment');
                    } else {
                        this.hideModalLoader();
                    }
                    if (onSuccess) {
                        onSuccess(response);
                    }
                },
                error: (response)=>{
                    console.log(response);
                    this.hideModalLoader();
                }
            });
        }
    }

    scopeDropZone(type,entity_id){
        if (type && entity_id) {
            const entityClass = CRUDFabric.getClass(type);
            const entity = new entityClass(entity_id);

            try {
                entity.scopeOpenDropZoneListener({}, () =>{
                    this.refreshDataAfterUploadFile(this.modal_id);
                }
                );
                entity.setAttachmentCallbackDelete();
            } catch (error) {
                console.log(error);
            }
        }
    }

    refreshDataAfterUploadFile(parent){
        const current_step = $(`#${parent}`).find('.main-step.active');
        if ($(current_step).data('type') == 'info') {
            this.#getStepData($(current_step).data('type'));
        }else{
            const step = $(`#${parent}`).find('.internal-step.active').data('step');
            const type = $(`#${parent}`).find('.internal-step.active').data('type');
            this.#getInternalStepData(null, type, step);
        }
    }

    hidedSelectBehavior(){
        $(`#${this.modal_id} .internal-data-container`).off('click', '.hided-select')
        .on('click', '.hided-select', function(e) {
            let opened_options = $('.hided-select-options.opened').toArray();
            opened_options.forEach(element => {
                if ($(element).is($(this).find('.hided-select-options.opened'))) {
                    return;
                }
                $(element).removeClass('opened');
            });
            $(this).find('.hided-select-options').toggleClass('opened');
        });
        $(`#${this.modal_id} .internal-data-container`).off('click', '.hided-select-option')
        .on('click', '.hided-select-option', function(e) {
            let parent = $(this).parents('.hided-select');
            parent.find('.selected-value').attr('value', $(this).attr('value'));
            parent.find('.selected-value').text($(this).text());
            parent.find('input').val($(this).attr('value'));
        });
    }

    workerSelectBehavior(){
        $(`#${this.modal_id} .internal-data-container`).off('click', '.workers-select')
        .on('click', '.workers-select', function(e){
            if (!$(e.target).hasClass('worker-row')) {
                const workers_container = $(this).find('.workers-list');
                workers_container.toggleClass('d-none');

                let posX = 0;
                let posY = 50;
                workers_container.css({left: posX, top: posY});
            }
        });

        $(`#${this.modal_id} .internal-data-container`).off('click', '.worker-row')
        .on('click', '.worker-row', function(e) {
            let parent = $(this).parents('.workers-select');
            let selectedValue = $(parent).find('.selected-workers').text();
            if ($(this).hasClass('selected')) {
                $(this).find('input').removeAttr('checked');
            } else {
                $(this).find('input').attr('checked', 'checked');
            }
            $(this).toggleClass('selected');
            let selecetedInputs = $(parent).find('.worker-row.selected').toArray();
            let result = '';
            selecetedInputs.forEach(element => {
                if (result == '') {
                    result += $(element).find('input').val();
                } else {
                    result += ', '+$(element).find('input').val();
                }
            });
            $(parent).find('.selected-workers').text(result);
        });
    }

    resizeCommentField() {
        $('body').off('input', `#${this.modal_id} #comments`).on('input', `#${this.modal_id} #comments`, function() {
            this.style.cssText = 'height: auto';
            this.style.cssText = 'height:' + (this.scrollHeight+16) + 'px';
        });
    }

    showMore(){
        $(`#${this.modal_id} .internal-data-container`).off('click', '.show-more-btn')
        .on('click', '.show-more-btn', function(e) {
            const curr_elem = $(this);
            let compressed_elem;
            if (curr_elem.hasClass('worker-comments')) {
                compressed_elem = curr_elem.find('.content-container');
            }else{
                compressed_elem = curr_elem.find('textarea, input');
            }
            if (e.target == this) {
                if (curr_elem.hasClass('opened')) {
                    curr_elem.removeClass('opened');
                    curr_elem[0].setAttribute('data-after', 'Show More');
                    compressed_elem.css({overflow:'', height: ''});
                } else {
                    let scrollH = compressed_elem[0].scrollHeight + 5;
                    compressed_elem.css({overflow:'auto', height: scrollH+'px'});
                    curr_elem[0].setAttribute('data-after', 'Hide');
                    curr_elem.addClass('opened');
                }
            }
        });

        $(`#${this.modal_id}`).off('click', '.show-more-attachments-btn')
        .on('click', '.show-more-attachments-btn', function(e) {
            const container = $(this);
            const entity = $(this).data('entity');
            const entity_id = $(this).data('entity_id');
            const step = $(this).data('step')+1;
            if (e.target == this) {
                $.ajax({
                    method: "POST",
                    url: '/jobs-flow/get-more-files',
                    data: {
                        entity: entity,
                        entity_id:entity_id,
                        step: step,
                    },
                    success: (r) => {
                        if (r.view) {
                            container.append(r.view);
                        }
                        if (r.many_files == false) {
                            container.removeClass('with-show-more');
                        }
                        $(this).data('step', step);
                    },
                    error: (r) => {

                    }
                });
            }
        });
    }

    /**
     * Send asks WHICH CHANNEL before it sends anything.
     *
     * It used to fire straight at /jobs-flow/send-sms on click: the button said
     * only "Send", the user could not tell an estimate would be TEXTED, and the
     * one confirmation was the label flipping to "Sended" — which only ever
     * appeared on the Invoice step, because the handler wrote into
     * `.find('div')` and the Estimate step's label is a <span>. A failure wrote
     * to the console and nothing else, so a customer who never received the
     * estimate looked exactly like one who did.
     *
     * Now the click opens the channel picker (the same shape as the "Link to
     * customer personal Cabinet" dialog), each option shows where it is actually
     * going, and the result is reported in the dialog.
     */
    sendEntityFromSms(){
        const modal = $('#send-channel-modal');

        $(`#${this.modal_id} .internal-data-container`).off('click', '.send-entity-to-sms')
        .on('click', '.send-entity-to-sms', (e)=>{
            const btn = $(e.currentTarget);
            const noun = btn.data('noun') || 'record';

            modal.find('.modal-custom-title').text(`Send ${noun}`);
            modal.find('.js-send-channel-lead').text(`How would you like to send this ${noun} to the customer?`);
            this.resetSendChannelRows(modal);

            // Remember what this dialog is about — the picker is a single shared
            // element, so nothing may be inferred from the last time it opened.
            modal.data('sendTarget', { type: btn.data('entity'), id: btn.data('id'), noun });

            ['sms', 'email'].forEach((channel) => {
                const row   = modal.find(`.js-send-channel[data-channel="${channel}"]`);
                const ready = String(btn.data(`${channel}-ready`)) === '1';
                const dest  = btn.data(`${channel}-dest`) || '';

                row.toggleClass('is-off', !ready)
                   .attr('aria-disabled', ready ? null : 'true')
                   .removeClass('is-busy');
                row.find('.js-send-channel-dest')
                   .text(ready ? dest : (btn.data(`${channel}-why`) || 'Not available.'))
                   .toggleClass('bb-choice__dest', ready)
                   .toggleClass('bb-choice__why', !ready);
            });

            modal.modal('show');
        });

        modal.off('click', '.js-send-channel')
        .on('click', '.js-send-channel', (e) => {
            const row     = $(e.currentTarget);
            const options = modal.find('.js-send-channel-options');
            const target  = modal.data('sendTarget') || {};

            if (row.hasClass('is-off') || options.hasClass('is-busy')) return;

            const lifecycle = window.BBButtonLifecycle;
            const name = row.find('.js-send-channel-name');
            const ic = row.find('.js-send-channel-ic');

            options.addClass('is-busy');
            row.addClass('is-busy');
            // The library's own spinner, in the row's icon well. A full
            // BBButtonLifecycle.start() would replace the row's children with a
            // bare label and throw away the channel name and destination — which
            // is the whole point of the row — so only the spinner is borrowed.
            ic.data('icon-html', ic.html()).html('<span class="bb-spin" aria-hidden="true"></span>');
            name.text('Sending…');
            if (lifecycle) lifecycle.setFeedback(row[0], {});

            $.ajax({
                method: 'POST',
                url: '/jobs-flow/send-entity',
                data: { type: target.type, id: target.id, channel: row.data('channel') },
                success: (r) => {
                    this.finishSendChannel(row, 'success', r?.message || 'Sent.');
                    // Let the confirmation be read before the dialog closes.
                    setTimeout(() => modal.modal('hide'), 2200);
                },
                error: (xhr) => {
                    this.finishSendChannel(row, 'error', xhr?.responseJSON?.message || 'This could not be sent. Please try again.');
                }
            });
        });
    }

    /** Put every channel row back to rest — the dialog is a single shared element. */
    resetSendChannelRows(modal){
        modal.find('.js-send-channel-options').removeClass('is-busy');
        modal.find('.js-send-channel').each((_, el) => {
            const row = $(el);
            row.removeClass('is-busy is-success is-error');
            const ic = row.find('.js-send-channel-ic');
            if (ic.data('icon-html')) ic.html(ic.data('icon-html'));
            const name = row.find('.js-send-channel-name');
            name.text(name.attr('data-default-label') || name.text());
        });
        modal.find('[data-bb-action-feedback]')
             .removeClass('is-visible is-success is-error')
             .empty();
    }

    /**
     * Report the outcome the way a Save reports it: the shared
     * .bb-action-feedback card driven by the same BBButtonLifecycle the save
     * buttons use, plus Sent / Try again on the row itself. Replaces the old
     * behaviour of writing "Sended" into the action-row button.
     */
    finishSendChannel(row, outcome, message){
        const lifecycle = window.BBButtonLifecycle;
        const ok = outcome === 'success';
        const name = row.find('.js-send-channel-name');
        const ic = row.find('.js-send-channel-ic');

        row.closest('.js-send-channel-options').removeClass('is-busy');
        row.removeClass('is-busy');
        if (ic.data('icon-html')) ic.html(ic.data('icon-html'));
        name.text(ok ? 'Sent' : 'Try again');
        row.toggleClass('is-success', ok).toggleClass('is-error', !ok);

        if (lifecycle) {
            lifecycle.setFeedback(row[0], { state: ok ? 'success' : 'error', message });
        }
    }

    initPagination(){
        $(`#${this.modal_id}`).off('click', '.bb-pg[data-step]')
        .on('click', '.bb-pg[data-step]', (e)=>{
            const step = $(e.currentTarget).data('step');
            if (step !== undefined) {
                this.#getStepData(this.currentStep, this.collectOptionsForStep(true, step), 150);
            }
        });
    }

    /**
     * Initialize pagination for customer appointments and subscriptions
     * This method should be called after loading the step data
     */
    initCustomerPagination() {
        // Generate pagination UI
        this.updateCustomerPagination();

        // Handle pagination clicks
        $(`#${this.modal_id}`).off('click', '.pagination-container .bb-pg[data-step]')
        .on('click', '.pagination-container .bb-pg[data-step]', (e) => {
            // Prevent default behavior
            e.preventDefault();

            // Get the page number from data attribute
            let pageNumber = $(e.currentTarget).data('step');

            // Handle previous/next buttons
            const currentPage = parseInt($(`#${this.modal_id}`).find('#curr_page').val() || 1);
            const totalPages = parseInt($(`#${this.modal_id}`).find('#total_pages').val() || 1);

            if (pageNumber === -1) {
                // Previous page
                pageNumber = Math.max(1, currentPage - 1);
            } else if (pageNumber === -2) {
                // Next page
                pageNumber = Math.min(totalPages, currentPage + 1);
            }

            // Collect current filter options and add the page number
            const options = this.collectOptionsForStep(true, pageNumber);

            // Load the new page
            this.#getStepData(this.currentStep, options, 0);
        });
    }

    /**
     * Update pagination UI for customer appointments and subscriptions
     * This method generates the pagination controls dynamically
     */
    updateCustomerPagination() {
        // Get pagination data from hidden inputs
        const currentPage = parseInt($(`#${this.modal_id}`).find('#curr_page').val() || 1);
        const totalPages = parseInt($(`#${this.modal_id}`).find('#total_pages').val() || 1);
        const tableSize = parseInt($(`#${this.modal_id}`).find('#table_size').val() || 10);

        // Find the pagination container
        const paginationContainer = $(`#${this.modal_id}`).find('.pagination-container')[0];

        if (!paginationContainer) return;

        // Clear the container
        paginationContainer.innerHTML = '';

        // Only show pagination if there's more than one page
        if (totalPages > 1) {
            paginationContainer.classList.add('d-flex', 'bb-pager');
            paginationContainer.classList.remove('d-none');

            // REAL .bb-pg component (no facelift). .page-number + data-step are the JS
            // hooks the click handler reads; .is-gap = ellipsis; arrows are -1/-2 glyphs.
            const createPageButton = (page, isActive = false) =>
                `<span class="bb-pg page-number ${isActive ? 'active' : ''}" data-step="${page}">${page}</span>`;
            const gap = () => `<span class="bb-pg is-gap">…</span>`;

            // Previous arrow
            paginationContainer.innerHTML += `<span class="bb-pg" data-step="-1">‹</span>`;

            // Windowed 1 2 3 4 5 … 9 10 when there are more than 9 pages
            if (totalPages > 9) {
                if (currentPage <= 4) {
                    for (let i = 1; i <= 5; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                    paginationContainer.innerHTML += gap();
                    for (let i = totalPages - 1; i <= totalPages; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                } else if (currentPage > totalPages - 4) {
                    for (let i = 1; i <= 2; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                    paginationContainer.innerHTML += gap();
                    for (let i = totalPages - 4; i <= totalPages; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                } else {
                    for (let i = 1; i <= 2; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                    paginationContainer.innerHTML += gap();
                    for (let i = currentPage - 1; i <= currentPage + 1; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                    paginationContainer.innerHTML += gap();
                    for (let i = totalPages - 1; i <= totalPages; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                }
            } else {
                for (let i = 1; i <= totalPages; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
            }

            // Next arrow
            paginationContainer.innerHTML += `<span class="bb-pg" data-step="-2">›</span>`;
        } else {
            paginationContainer.classList.remove('d-flex', 'bb-pager');
            paginationContainer.classList.add('d-none');
        }
    }

    /**
     * Update pagination UI with the same logic as stafter-table
     * @param {number} totalCount - Total number of items
     * @param {number} limit - Items per page
     * @param {number} currentPage - Current page number
     */
    updatePagination(totalCount, limit, currentPage) {
        let totalPages = Math.ceil(totalCount / limit);
        let paginationContainer = $(`#${this.modal_id}`).find('.stafter-table-paginator .pagination-container')[0];

        if (!paginationContainer) return;
        paginationContainer.innerHTML = '';

        if (totalPages > 1) {
            paginationContainer.classList.add('d-flex', 'bb-pager');
            paginationContainer.classList.remove('d-none');

            // REAL .bb-pg component (no facelift). data-step = the page to load (JS hook);
            // .is-gap = ellipsis; arrows carry the prev/next page number as data-step.
            let createPageButton = (page, isActive = false) =>
                `<div class="bb-pg page-number ${isActive ? 'active' : ''}" data-step="${page}">${page}</div>`;
            let gap = () => `<div class="bb-pg is-gap">…</div>`;

            const prevPage = Math.max(1, currentPage - 1);
            paginationContainer.innerHTML += `<div class="bb-pg" data-step="${prevPage}">‹</div>`;

            // Windowed 1 2 3 4 5 … 9 10 when there are more than 9 pages
            if (totalPages > 9) {
                if (currentPage <= 4) {
                    for (let i = 1; i <= 5; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                    paginationContainer.innerHTML += gap();
                    for (let i = totalPages - 1; i <= totalPages; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                } else if (currentPage > totalPages - 4) {
                    for (let i = 1; i <= 2; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                    paginationContainer.innerHTML += gap();
                    for (let i = totalPages - 4; i <= totalPages; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                } else {
                    for (let i = 1; i <= 2; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                    paginationContainer.innerHTML += gap();
                    for (let i = currentPage - 1; i <= currentPage + 1; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                    paginationContainer.innerHTML += gap();
                    for (let i = totalPages - 1; i <= totalPages; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
                }
            } else {
                for (let i = 1; i <= totalPages; i++) paginationContainer.innerHTML += createPageButton(i, i === currentPage);
            }

            const nextPage = Math.min(totalPages, currentPage + 1);
            paginationContainer.innerHTML += `<div class="bb-pg" data-step="${nextPage}">›</div>`;
        } else {
            paginationContainer.classList.remove('d-flex', 'bb-pager');
            paginationContainer.classList.add('d-none');
        }

        // Re-initialize the click handlers
        this.initPagination();
    }

    /**
     *
     * @param {String} str String or text
     */
    ucFirst(str){
        if (str.split(' ').length>1) {
            let array_words = str.split(' ');
            let result = '';
            array_words.forEach((element, index) => {
                result+=element.charAt(0).toUpperCase() + element.slice(1)
                if (index < array_words.length) {
                    result+=' ';
                }
            });
            return result;
        }else{
            return str.charAt(0).toUpperCase() + str.slice(1);
        }
    }

    initLogs(){
        // Payment info button handler is already set up in initInternalComponents
        // No need to duplicate it here
        $(`#${this.modal_id}`).off('click','.entity-logs-btn')
        .on('click', '.entity-logs-btn', function() {
            const entity = $(this).data('entity');
            const entity_id = $(this).data('entity_id');
            const EntityObj = CRUDFabric.getObject(entity, entity_id);
            EntityObj.openModal('logs', {}, false);
        })
    }

    changeFileVisibility(){
        $(`#${this.modal_id}`).off('click', '.change-file-visibility')
        .on('click', '.change-file-visibility', (e)=>{
            $.ajax({
                method: 'POST',
                url: `/customers/${this.customer_id}/change-file-visibility`,
                data: {
                    file: $(e.currentTarget).data('file-id')
                },
                success: (r)=>{
                    this.refreshCurrentStep();
                },
                error: (r)=>{}
            });
        })
    }

    refreshCurrentStep(){
        if (this.currentStep == 'info' || this.currentStep == 'overview') {
            this.#getStepData(this.currentStep, {});
        }else{
            if (this.currentInternalStep) {
                this.#getInternalStepData(null, this.internalType, this.currentInternalStep);
            }else{
                this.#getStepData(this.currentStep, {});
            }
        }
    }
}
